import CalculateIcon from '@mui/icons-material/Calculate';
import {
    Box,
    Backdrop,
    Button,
    Card,
    CircularProgress,
    Divider,
    Drawer,
    FormControlLabel,
    InputAdornment,
    Switch,
    TextField,
    Typography,
    Paper,
    Chip,
    IconButton,
    Stack,
    Alert,
    Fade,
    Snackbar,
    useMediaQuery,
    List,
    ListItemButton,
    ListItemText,
    Breadcrumbs,
    Link
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTheme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import { useFormik } from 'formik';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import * as Yup from 'yup';
import { httpClient } from '../../common/httpclient';
import DialogForm from '../../component/dialog/DialogForm';
import Dropdown from '../../component/dropdown/Dropdown';
import PageScaffold from '../../component/pageScaffold/PageScaffold';
import { sidebarExpandedSelector } from '../../component/layout/sidebarState';
import PhotoIcon from '@mui/icons-material/Photo';
import Skeleton from '@mui/material/Skeleton';
import Badge from '@mui/material/Badge';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import DeleteIcon from '@mui/icons-material/Delete';
import InventoryIcon from '@mui/icons-material/Inventory';
import CategoryIcon from '@mui/icons-material/Category';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import DescriptionIcon from '@mui/icons-material/Description';
import BusinessIcon from '@mui/icons-material/Business';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import InfoIcon from '@mui/icons-material/Info';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import BarcodeScannerDialog from '../../component/barcode/BarcodeScannerDialog';
import { formatCurrency } from '../../common/formatters';
import { usePermissions } from '../../hooks/usePermissions';
import { useIsMobileShell } from '../../hooks/useIsMobileShell';
import MobileProductForm from '../../component/product/MobileProductForm';
import { useAppBarTitle } from '../../component/layout/AppBarActions';
import { getEnterpriseIdFromToken } from '../../component/context/EnterpriseConfigContext';
import {
    Package as PackageIcon,
    DollarSign as PriceIcon,
    Image as ImageSectionIcon,
    ImagePlus,
    Wand2,
    Calculator as CalculatorLucideIcon,
    ChevronDown,
    Check,
    Eye,
    Trash2,
    Info as InfoLucideIcon,
    Plus,
    Search,
    ChevronRight,
    AlertTriangle,
} from 'lucide-react';

/**
 * Seção colapsável do formulário de inserção (DESIGN-SYSTEM.md §5.5
 * "Accordion insertion form" / §6.2). Componente de módulo (fora do render de
 * `Form`) para não perder foco dos inputs a cada re-render — ver nota no
 * relatório do engenheiro.
 */
function AccordionSection({ open, complete, icon: Icon, title, subtitle, onToggle, children }) {
    const theme = useTheme();
    return (
        <Box
            sx={{
                border: '1.5px solid',
                borderColor: open ? 'primary.main' : 'divider',
                borderRadius: `${theme.customRadius.hero}px`,
                overflow: 'hidden',
                backgroundColor: 'background.paper',
                boxShadow: open ? `0 0 0 3px ${theme.palette.info.bg}` : 'none',
                transition: 'border-color .2s, box-shadow .2s',
            }}
        >
            <Box
                onClick={onToggle}
                sx={{ display: 'flex', alignItems: 'center', gap: 1.75, p: '18px 20px', cursor: 'pointer' }}
            >
                <Box
                    sx={{
                        width: 42,
                        height: 42,
                        borderRadius: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        backgroundImage: theme.palette.gradient.brand,
                        color: '#fff',
                    }}
                >
                    <Icon size={19} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: '14.5px', fontWeight: 700 }}>{title}</Typography>
                    <Typography sx={{ fontSize: '11.5px', color: 'text.disabled' }}>{subtitle}</Typography>
                </Box>
                {complete && (
                    <Box
                        sx={{
                            width: 22,
                            height: 22,
                            borderRadius: '50%',
                            backgroundColor: 'success.main',
                            color: '#fff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                        }}
                    >
                        <Check size={12} strokeWidth={3} />
                    </Box>
                )}
                <ChevronDown
                    size={16}
                    color={theme.palette.text.disabled}
                    style={{ transition: 'transform .2s', transform: open ? 'rotate(180deg)' : 'none', flexShrink: 0 }}
                />
            </Box>
            <Box sx={{ maxHeight: open ? 2000 : 0, overflow: 'hidden', transition: 'max-height .35s ease' }}>
                {/* Padding-top maior que o `2px` do protótipo (`.acc-body-inner`):
                    lá o rótulo flutuante fica inteiramente DENTRO da caixa do
                    campo (`.ff-label` vai de `top:17px` a `top:8px`, nunca sai
                    da box); aqui usamos o `TextField variant="outlined"`
                    nativo do MUI (DESIGN-SYSTEM.md §9), cujo rótulo "encolhido"
                    sobe para ~9px ACIMA da borda da caixa. Com só 2px de
                    padding-top, esse rótulo era cortado pelo `overflow:hidden`
                    do wrapper de animação acima sempre que o primeiro campo de
                    uma seção era um `TextField` (ex.: Quantidade, no topo de
                    "Preço e estoque") em vez de um `Dropdown` (rótulo estático,
                    sem esse problema). */}
                <Box sx={{ p: '16px 20px 24px' }}>{children}</Box>
            </Box>
        </Box>
    );
}

function PreviewRow({ label, value, highlight }) {
    return (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '12.5px', py: '6px', color: 'text.secondary' }}>
            <span>{label}</span>
            <Box component="b" sx={{ color: highlight ? 'success.main' : 'text.primary' }}>{value}</Box>
        </Box>
    );
}

export default function Form({ embedded = false, productId, onClose, onSuccess }) {
    const theme = useTheme();
    // Shell mobile (página cheia): formulário do protótipo `produtos/form.html`; o modo `embedded` não muda.
    const isMobileShell = useIsMobileShell();
    const mobileForm = !embedded && isMobileShell;
    // Bloco "Marketplace" só aparece quando o plano inclui o módulo.
    const { hasModule } = usePermissions();
    // Largura real do menu lateral (`ModernSidebar.js`) — usada para alinhar
    // o rodapé de ações fixo (`.action-footer` do protótipo) com a borda do
    // conteúdo em vez da borda da viewport. Ref: DESIGN-SYSTEM.md §3.1.
    const isSidebarMobile = useMediaQuery('(max-width:1023.95px)');
    const isSidebarExpanded = useRecoilValue(sidebarExpandedSelector);
    const actionFooterLeft = isSidebarMobile ? 0 : (isSidebarExpanded ? 264 : 76);
    const [categories, setCategories] = useState([]);
    const [subCategories, setSubCategories] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [scannerOpen, setScannerOpen] = useState(false);
    const [message, setMessage] = useState('');
    const [severity, setSeverity] = useState('');
    const navigate = useNavigate();
    const location = useLocation();
    // Veio da lista: volta por histórico (POP) para a lista restaurar os filtros.
    const goToList = () => (location.state?.fromList ? navigate(-1) : navigate('/products'));
    const { id: idParam } = useParams();
    const id = embedded ? productId : idParam;
    const [openDialogNewCategory, setOpenDialogNewCategory] = useState(false);
    const [errorCategoryName, setErrorCategoryName] = useState('');
    const [errorCategoryDescription, setErrorCategoryDescription] = useState('');
    const [categoryName, setCategoryName] = useState('');
    const [categoryDescription, setCategoryDescription] = useState('');
    const [openDialogNewSubCategory, setOpenDialogNewSubCategory] = useState(false);
    const [errorSubCategoryName, setErrorSubCategoryName] = useState('');
    const [errorSubCategoryDescription, setErrorSubCategoryDescription] = useState('');
    const [errorSubCategoryProfitMargin, setErrorSubCategoryProfitMargin] = useState('');
    const [subCategoryName, setSubCategoryName] = useState('');
    const [subCategoryDescription, setSubCategoryDescription] = useState('');
    const [subCategoryProfitMargin, setSubCategoryProfitMargin] = useState('');
    const [openDialogCalculateSalePrice, setOpenDialogCalculateSalePrice] = useState(false);
    const [profitPercentage, setProfitPercentage] = useState('');
    const [openDialogSuggestedPrice, setOpenDialogSuggestedPrice] = useState(false);
    const [suggestedPrice, setSuggestedPrice] = useState('');
    const [categoryProfitMargin, setCategoryProfitMargin] = useState(null);
    const [loading, setLoading] = useState(false);
    const [previewImage, setPreviewImage] = useState('');
    const [image, setImage] = useState(null);
    const [dragActive, setDragActive] = useState(false);
    // Seções do acordeão abertas de forma independente (não exclusiva): cada
    // seção guarda seu próprio estado de expansão, permitindo múltiplas
    // seções abertas ao mesmo tempo.
    const [openSections, setOpenSections] = useState(mobileForm ? ['info', 'price'] : ['info']);
    const isSectionOpen = (key) => openSections.includes(key);
    const toggleSection = (key) => setOpenSections((prev) => (
        prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    ));
    const ensureSectionOpen = (key) => setOpenSections((prev) => (
        prev.includes(key) ? prev : [...prev, key]
    ));
    const [openDialogMarketplaceAttributes, setOpenDialogMarketplaceAttributes] = useState(false);
    const [marketplaceAttributes, setMarketplaceAttributes] = useState([]);
    const [marketplaceAttributesLoading, setMarketplaceAttributesLoading] = useState(false);
    const [marketplaceAttributeValues, setMarketplaceAttributeValues] = useState({});
    const [marketplaceAttributeUnits, setMarketplaceAttributeUnits] = useState({});
    const [marketplaceCategories, setMarketplaceCategories] = useState([]);
    const [marketplaceCategoriesLoading, setMarketplaceCategoriesLoading] = useState(false);
    const [marketplaceCategoryId, setMarketplaceCategoryId] = useState('');
    const [marketplaceCondition, setMarketplaceCondition] = useState('');
    const [marketplaceListingTypeId, setMarketplaceListingTypeId] = useState('');
    // Valor de venda usado apenas na publicação no Mercado Livre (não altera o
    // sale_price cadastrado). Pré-preenchido com o valor de venda atual.
    const [marketplacePrice, setMarketplacePrice] = useState('');
    const [openDialogRemoveMarketplace, setOpenDialogRemoveMarketplace] = useState(false);
    // Navegação manual na árvore de categorias do Mercado Livre, usada quando
    // a busca automática por título (fetchMarketplaceCategories) não sugere
    // nenhuma categoria ("Não achei a categoria").
    const [categoryTreeOpen, setCategoryTreeOpen] = useState(false);
    const [categoryTreePath, setCategoryTreePath] = useState([]);
    const [categoryTreeNodes, setCategoryTreeNodes] = useState([]);
    const [categoryTreeLoading, setCategoryTreeLoading] = useState(false);
    const isEditing = Boolean(id);
    useAppBarTitle(mobileForm && isEditing ? 'Editar produto' : '');

    const fetchSuppliers = async () => {
        const response = await httpClient.get('/stock/suppliers');
        const responseData = response.data;
        setSuppliers(responseData);
    }

    const [isLoadingInput, setIsLoadingInput] = useState(true);

    const fetchProduct = async () => {
        try {
            const response = await httpClient.get(`/stock/edit/${id}`);
            const responseData = response.data;
            const hasResponseData = responseData && typeof responseData === 'object';
            const hasMercadoLivreIntegration = hasResponseData
                && Array.isArray(responseData.marketplace_integrations)
                && responseData.marketplace_integrations.some(
                    (integration) => integration
                        && integration.marketplace === 'mercadolivre'
                        && integration.enabled
                );
            formik.setValues({
                ...formik.values,
                ...(hasResponseData ? responseData : {}),
                mercado_livre_create: hasMercadoLivreIntegration
                    ? true
                    : (hasResponseData && responseData.mercado_livre_create !== undefined
                        ? responseData.mercado_livre_create
                        : formik.values.mercado_livre_create),
                mercado_livre_sync: hasResponseData && responseData.mercado_livre_sync !== undefined
                    ? responseData.mercado_livre_sync
                    : formik.values.mercado_livre_sync,
                mercado_livre_remove: hasResponseData && responseData.mercado_livre_remove !== undefined
                    ? responseData.mercado_livre_remove
                    : formik.values.mercado_livre_remove
            });
            if (responseData.category) {
                await fetchSubCategories(responseData.category);
            }
        } catch (error) {
            console.error('Error fetching product:', error);
        }
    }

    const fetchCategories = async () => {
        try {
            const response = await httpClient.get('/stock/categories');
            const responseData = response.data;
            setCategories(responseData);
            return responseData;
        } catch (error) {
            console.error('Error fetching categories:', error);
        }
    };

    const fetchSubCategories = async (selectedCategory) => {
        try {
            const response = await httpClient.get(`/stock/subcategories?categoryId=${selectedCategory}`);
            const responseData = response.data;
            setSubCategories(responseData);
            return responseData;
        } catch (error) {
            console.error('Error fetching subcategories:', error);
            return [];
        }
    }

    const validationSchema = Yup.object({
        code: Yup.string()
            .required('Código é obrigatório')
            .test('code', 'Código já existe', async value => {
                if (id) {
                    return true;
                }                
                const response = await httpClient.get(`/stock/productId?code=${value}`);
                formik.touched.code = true;
                return response.data.id === '';
            }),
        name: Yup.string().required('Nome é obrigatório'),
        quantity: Yup.number()
            .required('Quantidade é obrigatória')
            .min(0, 'Quantidade deve ser maior ou igual a 0'),
        min_quantity: Yup.number()
            .required('Quantidade mínima é obrigatória')
            .min(0, 'Quantidade mínima deve ser maior ou igual a 0'),
        category: Yup.string()
            .required('Categoria é obrigatória')
            .test('category', 'Categoria é obrigatória', value => value !== ''),
        // cost: Yup.string()
        //     .required('Valor do custo é obrigatório')
        //     .test('cost', 'Valor do custo deve ser maior que 0,00', value => {
        //         if (formik.values.is_generic) {
        //             return true;
        //         }
        //         if (!value) return true;
        //         return value !== '0,00';
        //     }),
        sale_price: Yup.string()
            .required('Valor de venda é obrigatório')
            .test('sale_price', 'Valor de venda deve ser maior que 0,00', value => {
                if (formik.values.is_generic) return true;
                
                if (!value) return true;
                
                return value !== '0,00';
            }),
        description: Yup.string().max(100, 'Descrição deve ter no máximo 100 caracteres')
    });

    const handleShowSuccessSnackbar = () => {
        setOpenSnackbar(true);
        if (id) {
            setMessage('Produto Atualizado com sucesso!');
        } else {
            setMessage('Produto Cadastrado com sucesso!');
        }
        setSeverity('success');
    }

    const handleShowErrorSnackbar = (responseData) => {
        setOpenSnackbar(true);
        // Handle both string and object responses
        if (typeof responseData === 'object' && responseData !== null) {
            setMessage(responseData.message || responseData.id || 'Erro desconhecido');
        } else {
            setMessage(responseData || 'Erro desconhecido');
        }
        setSeverity('error');
    }

    useEffect(() => {
        fetchCategories();
        fetchSuppliers();

        if (id) {
            setIsLoadingInput(true);
            fetchProduct().then(() => {
                setIsLoadingInput(false);
            });
        } else {
            setIsLoadingInput(false);
        }
    }, [id]);

    const formik = useFormik({
        initialValues: {
            code: '',
            name: '',
            quantity: '',
            min_quantity: '',
            supplier: '',
            category: '',
            subcategory: '',
            cost: '',
            sale_price: '',
            description: '',
            is_generic: false,
            image: '',
            mercado_livre_create: false,
            mercado_livre_sync: false,
            mercado_livre_remove: false
        },
        validationSchema: validationSchema,
        onSubmit: (values) => {
            if (id) {
                handleUpdateProduct();
            } else {
                handleCreateProduct();
            }
        },
    });

    const fetchCategoryDetails = async (categoryId) => {
        try {
            const response = await httpClient.get(`/category/${categoryId}`);
            const category = response.data.category;
            return category;
        } catch (error) {
            console.error('Error fetching category details:', error);
            return null;
        }
    }

    /** Custo no formulário pode vir como número (API) ou string formatada — API de cálculo espera string estilo BR. */
    const normalizePriceCostForApi = (cost) => {
        if (cost === null || cost === undefined || cost === '') return '';
        if (typeof cost === 'number') {
            if (Number.isNaN(cost)) return '';
            return cost.toFixed(2).replace('.', ',');
        }
        return String(cost).trim();
    };

    /** Resposta do cálculo pode vir como número ou string — campo de venda usa o mesmo formato de máscara. */
    const formatPriceSellerFromApi = (price) => {
        if (price === null || price === undefined) return '';
        if (typeof price === 'number' && !Number.isNaN(price)) {
            return formatCurrency(String(Math.round(price * 100 + Number.EPSILON)));
        }
        const s = String(price).trim();
        if (s === '') return '';
        if (s.includes(',')) {
            return s;
        }
        const n = parseFloat(s);
        if (!Number.isNaN(n)) {
            return formatCurrency(String(Math.round(n * 100 + Number.EPSILON)));
        }
        return s;
    };

    const calculateSuggestedPrice = async (
        priceCost,
        profitMargin,
        subcategoryId = '',
        categoryId = ''
    ) => {
        try {
            const calculateSalePrice = {
                price_cost: normalizePriceCostForApi(priceCost),
                percent: profitMargin,
                price_seller: '',
                subcategory_id: subcategoryId || '',
                category_id: categoryId || '',
                manual_calc: false
            };

            const response = await httpClient.post('/stock/calculate-price-seller', calculateSalePrice);
            if (response.status !== 200) {
                return null;
            }
            const raw = response.data?.price_seller;
            if (raw === null || raw === undefined || raw === '') {
                return null;
            }
            return formatPriceSellerFromApi(raw);
        } catch (error) {
            console.error('Error calculating suggested price:', error);
            return null;
        }
    }

    const isValidCostValue = (value) => value && value !== '' && value !== '0,00';

    const extractValidMargin = (value) => {
        if (value === null || value === undefined || value === '') return null;
        const parsed = parseFloat(String(value).replace(',', '.'));
        return Number.isNaN(parsed) ? null : parsed;
    };

    const resolveMarginForCurrentSelection = async (subcategoryId, categoryId) => {
        if (subcategoryId) {
            let selectedSubCategory = subCategories.find(
                (s) => String(s.id) === String(subcategoryId) || s.id === subcategoryId
            );

            if (!selectedSubCategory && categoryId) {
                const fetchedSubCategories = await fetchSubCategories(categoryId);
                selectedSubCategory = (fetchedSubCategories || []).find(
                    (s) => String(s.id) === String(subcategoryId) || s.id === subcategoryId
                );
            }

            // Fallback: alguns endpoints de subcategoria não retornam margem.
            // Nesses casos, buscamos na categoria completa para priorizar a margem da subcategoria.
            if ((!selectedSubCategory || selectedSubCategory?.profit_margin === null || selectedSubCategory?.profit_margin === undefined) && categoryId) {
                try {
                    const response = await httpClient.get(`/category/${categoryId}`);
                    const responseData = response.data || {};
                    const categorySubCategories = responseData.sub_categories || responseData.subcategories || responseData.category?.sub_categories || responseData.category?.subcategories || [];
                    const foundSubCategory = (categorySubCategories || []).find(
                        (s) => String(s.id) === String(subcategoryId) || s.id === subcategoryId
                    );
                    if (foundSubCategory) {
                        selectedSubCategory = foundSubCategory;
                    }
                } catch (error) {
                    console.error('Error fetching subcategory margin from category details:', error);
                }
            }

            const subMargin = extractValidMargin(selectedSubCategory?.profit_margin);
            if (subMargin !== null) {
                return subMargin;
            }
        }

        if (!categoryId) return null;

        const currentCategoryMargin = extractValidMargin(categoryProfitMargin);
        if (currentCategoryMargin !== null) {
            return currentCategoryMargin;
        }

        const category = await fetchCategoryDetails(categoryId);
        const categoryMargin = extractValidMargin(category?.profit_margin);
        if (categoryMargin !== null) {
            setCategoryProfitMargin(category.profit_margin);
            return categoryMargin;
        }

        return null;
    };

    const handleChangeCost = (event) => {
        const value = formatCurrency(event.target.value);
        formik.setFieldValue('cost', value);
    };

    const handleBlurCost = async (event) => {
        formik.handleBlur(event);
        const value = formatCurrency(event.target.value);
        if (value !== formik.values.cost) {
            formik.setFieldValue('cost', value);
        }
        if (!isValidCostValue(value)) return;

        const selectedCategory = formik.values.category;
        const selectedSubCategory = formik.values.subcategory;
        if (!selectedCategory && !selectedSubCategory) return;

        const marginToUse = await resolveMarginForCurrentSelection(selectedSubCategory, selectedCategory);
        if (marginToUse == null || Number(marginToUse) <= 0) return;
        setCategoryProfitMargin(marginToUse);

        const suggested = await calculateSuggestedPrice(
            value,
            marginToUse,
            selectedSubCategory,
            selectedCategory
        );

        const hasSuggestedPrice = suggested !== null && suggested !== undefined && suggested !== '';
        if (hasSuggestedPrice) {
            setSuggestedPrice(suggested);
            setOpenDialogSuggestedPrice(true);
        }
    };

    const handleChangeCategory = async (value) => {
        formik.setFieldValue('category', value);
        formik.setFieldValue('subcategory', '');
        await fetchSubCategories(value);
        
        // Buscar detalhes da categoria e calcular preço sugerido
        if (value) {
            const category = await fetchCategoryDetails(value);
            
            if (category && category.profit_margin !== null && category.profit_margin !== undefined) {
                setCategoryProfitMargin(category.profit_margin);
                
                
                // Se a margem for zero, não sugerir preço
                if (Number(category.profit_margin) === 0) {
                    setSuggestedPrice('');
                    setOpenDialogSuggestedPrice(false);
                    return;
                }

                // Se houver preço de custo, calcular e mostrar o preço sugerido
                const priceCost = formik.values.cost;
                if (priceCost && priceCost !== '' && priceCost !== '0,00') {
                    const suggested = await calculateSuggestedPrice(
                        priceCost,
                        category.profit_margin,
                        formik.values.subcategory,
                        value
                    );
                    if (suggested) {
                        setSuggestedPrice(suggested);
                        setOpenDialogSuggestedPrice(true);
                    }
                }
            }
        }
    }

    const handleChangeSubCategory = async (value) => {
        formik.setFieldValue('subcategory', value);

        if (!value) {
            setOpenDialogSuggestedPrice(false);
            return;
        }

        const selectedCategory = formik.values.category;
        const marginToUse = await resolveMarginForCurrentSelection(value, selectedCategory);

        if (marginToUse == null || Number(marginToUse) <= 0) {
            setSuggestedPrice('');
            setOpenDialogSuggestedPrice(false);
            return;
        }

        setCategoryProfitMargin(marginToUse);

        const priceCost = formik.values.cost;
        if (!isValidCostValue(priceCost)) {
            return;
        }

        const suggested = await calculateSuggestedPrice(
            priceCost,
            marginToUse,
            value,
            selectedCategory
        );

        const hasSuggestedPrice = suggested !== null && suggested !== undefined && suggested !== '';
        if (hasSuggestedPrice) {
            setSuggestedPrice(suggested);
            setOpenDialogSuggestedPrice(true);
        }
    }

    const handleChangeSupplier = (value) => {
        formik.setFieldValue('supplier', value);
    }

    const fetchMarketplaceAttributes = async (categoryId) => {
        if (!categoryId) {
            setMarketplaceAttributes([]);
            setMarketplaceAttributeValues({});
            return;
        }
        setMarketplaceAttributesLoading(true);
        try {
            const response = await httpClient.get(
                `/marketplace/ml/attributes?category_id=${categoryId}`
            );
            const responseData = Array.isArray(response.data) ? response.data : [];
            setMarketplaceAttributes(responseData);

            // Pré-preenche o atributo "Código Universal de Produto" (GTIN) do Mercado
            // Livre com o código interno cadastrado do produto.
            const universalProductCodeAttribute = responseData.find(
                (attribute) => attribute.id === 'GTIN'
            );
            if (universalProductCodeAttribute && formik.values.code) {
                setMarketplaceAttributeValues((prev) => (
                    prev[universalProductCodeAttribute.id]
                        ? prev
                        : { ...prev, [universalProductCodeAttribute.id]: formik.values.code }
                ));
            }
        } catch (error) {
            console.error('Error fetching marketplace attributes:', error);
            setMessage('Erro ao buscar atributos do Mercado Livre.');
            setSeverity('error');
            setOpenSnackbar(true);
        } finally {
            setMarketplaceAttributesLoading(false);
        }
    };

    const fetchMarketplaceCategories = async (title) => {
        if (!title) {
            setMessage('Informe o nome do produto para carregar as categorias.');
            setSeverity('error');
            setOpenSnackbar(true);
            return;
        }
        setMarketplaceCategoriesLoading(true);
        try {
            const enterpriseId = getEnterpriseIdFromToken();
            const queryString = enterpriseId
                ? `?title=${encodeURIComponent(title)}&enterprise_id=${encodeURIComponent(enterpriseId)}`
                : `?title=${encodeURIComponent(title)}`;
            const response = await httpClient.get(`/marketplace/ml/categories${queryString}`);
            const responseData = Array.isArray(response.data) ? response.data : [];
            const normalizedCategories = responseData.map((category) => ({
                id: category.category_id,
                name: category.category_name
            }));
            setMarketplaceCategories(normalizedCategories);
        } catch (error) {
            console.error('Error fetching marketplace categories:', error);
            setMessage('Erro ao buscar categorias do Mercado Livre.');
            setSeverity('error');
            setOpenSnackbar(true);
        } finally {
            setMarketplaceCategoriesLoading(false);
        }
    };

    // Busca um nível da árvore de categorias do Mercado Livre. Sem
    // categoryId, retorna as categorias raiz do site; com categoryId,
    // retorna as subcategorias diretas dela e informa se ela já é uma
    // categoria final (sem subcategorias).
    const fetchCategoryTreeLevel = async (categoryId) => {
        setCategoryTreeLoading(true);
        try {
            // O Mercado Livre passou a exigir um token OAuth válido também nos
            // endpoints de categoria do site (erro 403
            // PA_UNAUTHORIZED_RESULT_FROM_POLICIES sem token); enviamos o
            // enterprise_id para o backend buscar o token já salvo da
            // integração conectada, mesmo padrão já usado em
            // `fetchMarketplaceCategories`.
            const params = new URLSearchParams();
            if (categoryId) {
                params.set('category_id', categoryId);
            }
            const enterpriseId = getEnterpriseIdFromToken();
            if (enterpriseId) {
                params.set('enterprise_id', enterpriseId);
            }
            const queryString = params.toString() ? `?${params.toString()}` : '';
            const response = await httpClient.get(`/marketplace/ml/categories/tree${queryString}`);
            // O `httpClient` (ver `common/httpclient.js`) aceita qualquer status
            // 200-599 sem lançar exceção (`validateStatus`), então um erro do
            // backend (ex.: rota inexistente, falha ao chamar o Mercado Livre)
            // precisa ser checado manualmente aqui, senão cai silenciosamente
            // no `catch` de baixo... na real, sem isso ele NÃO cai no catch e
            // a árvore fica vazia sem nenhum aviso ao usuário.
            if (response.status < 200 || response.status >= 300) {
                throw new Error(
                    typeof response.data === 'string' ? response.data : 'Erro ao buscar categorias do Mercado Livre.'
                );
            }
            const responseData = response.data || {};
            const nodes = Array.isArray(responseData.categories)
                ? responseData.categories.map((category) => ({ id: category.id, name: category.name }))
                : [];
            return { nodes, isLeaf: Boolean(responseData.is_leaf) };
        } catch (error) {
            console.error('Error fetching marketplace category tree:', error);
            setMessage('Erro ao buscar categorias do Mercado Livre.');
            setSeverity('error');
            setOpenSnackbar(true);
            return { nodes: [], isLeaf: false };
        } finally {
            setCategoryTreeLoading(false);
        }
    };

    // Abre a navegação manual de categorias ("Não achei a categoria"),
    // carregando as categorias raiz do Mercado Livre.
    const handleOpenCategoryTree = async () => {
        setCategoryTreeOpen(true);
        setCategoryTreePath([]);
        const { nodes } = await fetchCategoryTreeLevel('');
        setCategoryTreeNodes(nodes);
    };

    const handleCloseCategoryTree = () => {
        setCategoryTreeOpen(false);
    };

    // Ao clicar em um nó da árvore: se ele não tiver subcategorias, é uma
    // categoria final e é selecionada como a categoria do anúncio; caso
    // contrário, o usuário desce mais um nível na árvore.
    const handleSelectCategoryTreeNode = async (node) => {
        const { nodes, isLeaf } = await fetchCategoryTreeLevel(node.id);
        if (isLeaf) {
            setMarketplaceCategories((prev) => (
                prev.some((category) => category.id === node.id)
                    ? prev
                    : [...prev, { id: node.id, name: node.name }]
            ));
            setMarketplaceCategoryId(node.id);
            setCategoryTreeOpen(false);
            await fetchMarketplaceAttributes(node.id);
            return;
        }
        setCategoryTreePath((prev) => [...prev, node]);
        setCategoryTreeNodes(nodes);
    };

    // Navega de volta para um nível anterior da árvore a partir da
    // trilha (breadcrumb). `index === -1` volta para a raiz.
    const handleCategoryTreeBreadcrumbClick = async (index) => {
        const newPath = index < 0 ? [] : categoryTreePath.slice(0, index + 1);
        const targetId = newPath.length ? newPath[newPath.length - 1].id : '';
        const { nodes } = await fetchCategoryTreeLevel(targetId);
        setCategoryTreePath(newPath);
        setCategoryTreeNodes(nodes);
    };

    const handleMarketplaceAttributeChange = (attributeId, value) => {
        setMarketplaceAttributeValues((prev) => ({
            ...prev,
            [attributeId]: value
        }));
    };

    const handleMarketplaceAttributeUnitChange = (attributeId, unitId) => {
        setMarketplaceAttributeUnits((prev) => ({
            ...prev,
            [attributeId]: unitId
        }));
    };

    const handleBarcodeDetected = (code) => {
        formik.setFieldValue('code', code);
        setMessage(`Código lido: ${code}`);
        setSeverity('success');
        setOpenSnackbar(true);
    };

    const handleSuggestCode = async () => {
        try {
            const categoryId = formik.values.category;
            const queryString = categoryId ? `?categoryId=${encodeURIComponent(categoryId)}` : '';
            const response = await httpClient.get(`/stock/suggest-code${queryString}`);
            const { suggested_code } = response.data || {};
            if (suggested_code != null) {
                formik.setFieldValue('code', suggested_code);
            }
        } catch (error) {
            console.error('Error fetching suggested code:', error);
        }
    };

    const handleOpenMarketplaceDialog = () => {
        setOpenDialogMarketplaceAttributes(true);
        setMarketplacePrice(formik.values.sale_price || '');
        fetchMarketplaceCategories(formik.values.name);
    };

    const handleCloseMarketplaceDialog = () => {
        setOpenDialogMarketplaceAttributes(false);
    };

    const handleResetMarketplaceDialog = () => {
        setOpenDialogMarketplaceAttributes(false);
        setMarketplaceCategories([]);
        setMarketplaceCategoriesLoading(false);
        setMarketplaceCategoryId('');
        setMarketplaceCondition('');
        setMarketplaceListingTypeId('');
        setMarketplacePrice('');
        setMarketplaceAttributes([]);
        setMarketplaceAttributesLoading(false);
        setMarketplaceAttributeValues({});
        setMarketplaceAttributeUnits({});
        setCategoryTreeOpen(false);
        setCategoryTreePath([]);
        setCategoryTreeNodes([]);
        setCategoryTreeLoading(false);
    };

    const handleMarketplaceToggleChange = (event) => {
        const isChecked = event.target.checked;
        const hasMarketplaceIntegrations = Array.isArray(formik.values.marketplace_integrations)
            && formik.values.marketplace_integrations.length > 0;

        if (isChecked) {
            formik.setFieldValue('mercado_livre_create', true);
            handleOpenMarketplaceDialog();
            return;
        }

        if (hasMarketplaceIntegrations) {
            setOpenDialogRemoveMarketplace(true);
            return;
        }

        formik.setFieldValue('mercado_livre_create', false);
    };

    const buildMarketplaceAttributesPayload = () => {
        return marketplaceAttributes
            .map((attribute) => {
                const selectedValue = marketplaceAttributeValues[attribute.id];
                if (selectedValue === undefined || selectedValue === null || selectedValue === '') {
                    return null;
                }

                const options = Array.isArray(attribute.values)
                    ? attribute.values
                    : (Array.isArray(attribute.value) ? attribute.value : []);
                const optionMatch = options.find((option) => String(option.id) === String(selectedValue));
                const allowedUnits = Array.isArray(attribute.allowed_units) ? attribute.allowed_units : [];
                const selectedUnitId = marketplaceAttributeUnits[attribute.id];
                const unitMatch = allowedUnits.find((unit) => String(unit.id) === String(selectedUnitId));
                const unitPayload = unitMatch
                    ? { value_unit: unitMatch.id }
                    : (selectedUnitId ? { value_unit: selectedUnitId } : {});
                const baseValueName = optionMatch ? optionMatch.name : selectedValue;
                const valueNameWithUnit = unitMatch && baseValueName
                    ? `${baseValueName} ${unitMatch.name}`
                    : baseValueName;

                if (optionMatch) {
                    return {
                        id: attribute.id,
                        value_id: optionMatch.id,
                        value_name: valueNameWithUnit,
                        ...unitPayload
                    };
                }

                return {
                    id: attribute.id,
                    value_name: valueNameWithUnit,
                    ...unitPayload
                };
            })
            .filter(Boolean);
    };

    const buildMarketplaceCategoriesPayload = () => {
        const marketplaceCategoriesPayload = [];
        const hasMercadoLivreIntegration = Boolean(formik.values.mercado_livre_create);

        if (hasMercadoLivreIntegration && marketplaceCategoryId) {
            marketplaceCategoriesPayload.push({
                marketplace: 'mercadolivre',
                category_id: marketplaceCategoryId,
                ...(marketplaceCondition ? { condition: marketplaceCondition } : {}),
                ...(marketplaceListingTypeId ? { listing_type_id: marketplaceListingTypeId } : {}),
                ...(marketplacePrice ? { price: marketplacePrice } : {}),
                attributes: buildMarketplaceAttributesPayload()
            });
        }

        return marketplaceCategoriesPayload;
    };

    const handleCreateProduct = async () => {
        setLoading(true);
        try {
            const marketplaceIntegrations = [];
            if (formik.values.mercado_livre_create) {
                marketplaceIntegrations.push({
                    marketplace: 'mercadolivre',
                    enabled: true,
                    action: 'create'
                });
            }

            const marketplaceCategoriesPayload = buildMarketplaceCategoriesPayload();
            const payload = {
                ...formik.values,
                marketplace_integrations: marketplaceIntegrations,
                marketplace_categories: marketplaceCategoriesPayload
            };

            const response = await httpClient.post('/stock', payload);
            if (response.status !== 201) {
                const responseData = response.data;
                let errorData = responseData;
                let errorMessage = '';
                try {
                    if (typeof errorData === 'string') {
                        // Try to parse string if possible
                        errorData = JSON.parse(errorData);
                    }
                    errorMessage = JSON.stringify(errorData, null, 2); // formatted JSON
                } catch (e) {
                    // fallback: display as is if not JSON
                    errorMessage = typeof errorData === 'string' ? errorData : 'Erro ao enviar imagem';
                }
                setMessage(errorMessage);
                handleShowErrorSnackbar(errorMessage);
                return;
            }

            if (image) {
                const formData = new FormData();
                formData.append('file', image);

                const responseUpload = await httpClient.postFile(`/stock/upload?productId=${response.data.id}&name=${response.data.id}`, formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                });

                if (responseUpload.status < 200 || responseUpload.status >= 300) {
                    // Parse the responseUpload.data as JSON and display it formatted in the snackbar
                    let errorData = responseUpload.data;
                    let errorMessage = '';
                    try {
                        if (typeof errorData === 'string') {
                            // Try to parse string if possible
                            errorData = JSON.parse(errorData);
                        }
                        errorMessage = JSON.stringify(errorData, null, 2); // formatted JSON
                    } catch (e) {
                        // fallback: display as is if not JSON
                        errorMessage = typeof errorData === 'string' ? errorData : 'Erro ao enviar imagem';
                    }
                    setMessage(errorMessage);
                    setSeverity('error');
                    setOpenSnackbar(true);
                    handleShowErrorSnackbar(responseUpload.data);
                    return;
                }
            }
            handleShowSuccessSnackbar();
            formik.resetForm();
            if (embedded && onSuccess) {
                try {
                    const res = await httpClient.get(`/stock/edit/${response.data.id}`);
                    onSuccess(res.data);
                } catch (e) {
                    onSuccess(response.data);
                }
            } else if (!embedded) {
                navigate(`/products/edit/${response.data.id}`);
            }
        } catch (error) {
            console.error('Error creating product:', error);
            handleShowErrorSnackbar();
        } finally {
            setLoading(false);
        }
    }

    const handleUpdateProduct = async () => {
        setLoading(true);
        try {
            const marketplaceIntegrations = [];
            const hasMarketplaceIntegrations = Array.isArray(formik.values.marketplace_integrations)
                && formik.values.marketplace_integrations.length > 0;
            const shouldPublishOnUpdate = Boolean(
                formik.values.mercado_livre_create || hasMarketplaceIntegrations
            );
            const shouldSendMarketplaceIntegration = Boolean(
                formik.values.mercado_livre_create || hasMarketplaceIntegrations
            );

            if (shouldSendMarketplaceIntegration) {
                const action = formik.values.mercado_livre_create
                    ? (hasMarketplaceIntegrations ? 'update' : 'create')
                    : 'delete';

                marketplaceIntegrations.push({
                    marketplace: 'mercadolivre',
                    enabled: formik.values.mercado_livre_create,
                    action
                });
            }
            const marketplaceCategoriesPayload = buildMarketplaceCategoriesPayload();
            const payload = {
                ...formik.values,
                mercado_livre_sync: shouldPublishOnUpdate,
                marketplace_integrations: marketplaceIntegrations,
                marketplace_categories: marketplaceCategoriesPayload
            };

            const response = await httpClient.put(`/stock/${id}`, payload);
            const isSuccess = response.status === 200 || response.status === 204;
            if (isSuccess) {
                handleShowSuccessSnackbar();
                await fetchProduct();
                if (embedded && onSuccess) {
                    try {
                        const res = await httpClient.get(`/stock/edit/${id}`);
                        onSuccess(res.data);
                    } catch (e) {
                        onSuccess();
                    }
                }
            } else {
                let errorData = response.data;
                let errorMessage = '';
                try {
                    if (typeof errorData === 'string') {
                        // Try to parse string if possible
                        errorData = JSON.parse(errorData);
                    }
                    errorMessage = JSON.stringify(errorData, null, 2); // formatted JSON
                } catch (e) {
                    // fallback: display as is if not JSON
                    errorMessage = typeof errorData === 'string' ? errorData : 'Erro ao enviar imagem';
                }
                setMessage(errorMessage);
                handleShowErrorSnackbar(errorMessage);
            }
        } catch (error) {
            console.error('Error updating product:', error);
            handleShowErrorSnackbar();
        } finally {
            setLoading(false);
        }
    }

    const handleNewCategory = () => {
        setOpenDialogNewCategory(true);
    }

    const handleNewSubCategory = () => {
        setOpenDialogNewSubCategory(true);
    }

    const handleUploadImage = async (file) => {
        try {
            const formData = new FormData();
            formData.append('file', file);
            const response = await httpClient.postFile(`/stock/upload?productId=${id}&name=${file.name}`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });
        } catch (error) {
            console.error('Error uploading image:', error);
            handleShowErrorSnackbar();
        }
    }

    // Fotos de câmera de celular chegam com vários MB (e às vezes HEIC) e estouram
    // limites de proxy/servidor. Redimensiona e converte para JPEG antes de enviar;
    // se o navegador não decodificar a imagem, segue com o arquivo original.
    const compressImage = (file, maxSize = 1600, quality = 0.85) => new Promise((resolve) => {
        const url = URL.createObjectURL(file);
        const img = new window.Image();
        img.onload = () => {
            try {
                const scale = Math.min(1, maxSize / Math.max(img.width, img.height));
                const canvas = document.createElement('canvas');
                canvas.width = Math.round(img.width * scale);
                canvas.height = Math.round(img.height * scale);
                canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
                canvas.toBlob((blob) => {
                    URL.revokeObjectURL(url);
                    if (!blob || blob.size >= file.size) return resolve(file);
                    const name = (file.name || 'imagem').replace(/\.[^.]+$/, '') + '.jpg';
                    resolve(new File([blob], name, { type: 'image/jpeg' }));
                }, 'image/jpeg', quality);
            } catch (e) {
                URL.revokeObjectURL(url);
                resolve(file);
            }
        };
        img.onerror = () => {
            URL.revokeObjectURL(url);
            resolve(file);
        };
        img.src = url;
    });

    const handleImageUpload = async (originalFile) => {
        if (originalFile) {
            const file = await compressImage(originalFile);
            const reader = new FileReader();
            reader.onload = (e) => {
                setImage(file);
                setPreviewImage(e.target.result);
            };
            reader.readAsDataURL(file);

            if (id) {
                handleUploadImage(file);
            }
        }
    }

    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") {
            setDragActive(true);
        } else if (e.type === "dragleave") {
            setDragActive(false);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);

        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            handleImageUpload(e.dataTransfer.files[0]);
        }
    };

    const removeImage = () => {
        setImage(null);
        setPreviewImage('');
        formik.setFieldValue('image', '');
    };

    const breadcrumbs = [
        { title: 'Produtos', link: '/products' },
    ];
    const hasMercadoLivreListing = Boolean(
        formik.values.mercado_livre_id ||
        formik.values.mercado_livre_item_id ||
        formik.values.mercado_livre_listing_id ||
        formik.values.ml_id ||
        formik.values.ml_item_id ||
        formik.values.ml_listing_id
    );
    const mercadoLivreUrl = Array.isArray(formik.values.marketplace_integrations)
        ? formik.values.marketplace_integrations.find(
            (integration) => integration
                && integration.marketplace === 'mercadolivre'
                && integration.url_product
        )?.url_product
        : '';
    const mercadoLivreLabel = (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography component="span">Mercado Livre</Typography>
            {mercadoLivreUrl && (
                <Typography
                    component="a"
                    href={mercadoLivreUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                        fontSize: '0.85rem',
                        color: 'primary.main',
                        textDecoration: 'none',
                        '&:hover': { textDecoration: 'underline' }
                    }}
                >
                    Ver anúncio
                </Typography>
            )}
        </Box>
    );

    // ---- Derivados do layout "acordeão + preview ao vivo" da página cheia
    // (`/products/new`, `/products/edit/:id`) — DESIGN-SYSTEM.md §5.5/§6.2,
    // Ref: prototipo-telas-insercao.html (Novo Produto). Não usados pelo modo
    // `embedded` (drawer da Lista de produtos), que mantém o layout anterior.
    const isFieldFilled = (fieldName) => {
        const value = formik.values[fieldName];
        if (value === null || value === undefined) return false;
        if (typeof value === 'boolean') return true;
        return String(value).trim().length > 0;
    };
    // `image` não é um campo obrigatório (não entra em `requiredFields`/no
    // cálculo do anel "Cadastro X%"), mas o chip e o acordeão "Imagem do
    // produto" precisam marcar como completo assim que houver uma imagem —
    // seja recém-selecionada (`previewImage`, ainda não salva) ou já
    // persistida (`formik.values.image`).
    const hasProductImage = Boolean(previewImage || formik.values.image);
    const standaloneSections = [
        {
            key: 'info',
            title: 'Informações básicas',
            subtitle: 'Nome, código, categoria e fornecedor',
            icon: PackageIcon,
            requiredFields: ['code', 'name', 'category'],
        },
        {
            key: 'price',
            title: 'Preço e estoque',
            subtitle: 'Custo, margem e quantidade disponível',
            icon: PriceIcon,
            requiredFields: ['quantity', 'min_quantity', 'sale_price'],
        },
        {
            key: 'media',
            title: 'Imagem do produto',
            subtitle: 'Foto exibida no sistema e nas vendas',
            icon: ImageSectionIcon,
            requiredFields: [],
        },
    ];
    // Regra de negócio: no cadastro de produto NOVO, o switch "Mercado
    // Livre" do card de preview só libera depois que "Informações básicas"
    // e "Preço e estoque" estiverem completas (mesmos `requiredFields` que
    // já marcam essas seções como completas nos chips/accordion acima).
    const infoAndPriceSectionsComplete = standaloneSections[0].requiredFields.every(isFieldFilled)
        && standaloneSections[1].requiredFields.every(isFieldFilled);
    const isMarketplaceSwitchDisabled = !isEditing && !infoAndPriceSectionsComplete;
    const requiredStandaloneSections = standaloneSections.filter((sec) => sec.requiredFields.length > 0);
    const completeStandaloneSectionsCount = requiredStandaloneSections.filter(
        (sec) => sec.requiredFields.every(isFieldFilled)
    ).length;
    const progressPct = requiredStandaloneSections.length > 0
        ? Math.round((completeStandaloneSectionsCount / requiredStandaloneSections.length) * 100)
        : 0;
    const RING_R = 23;
    const RING_CIRC = 2 * Math.PI * RING_R;
    const ringOffset = RING_CIRC - (RING_CIRC * progressPct / 100);

    const parseBRLNumber = (value) => {
        if (value === null || value === undefined || value === '') return 0;
        const normalized = String(value).replace(/\./g, '').replace(',', '.');
        const parsed = parseFloat(normalized);
        return Number.isNaN(parsed) ? 0 : parsed;
    };
    const costNumberValue = parseBRLNumber(formik.values.cost);
    const saleNumberValue = parseBRLNumber(formik.values.sale_price);
    const marginPct = saleNumberValue > 0
        ? Math.max(0, Math.min(100, ((saleNumberValue - costNumberValue) / saleNumberValue) * 100))
        : 0;
    const marginLabel = saleNumberValue > 0 ? `${marginPct.toFixed(1)}%` : '—';
    // Alerta sutil: margem esperada vem da subcategoria (ou categoria) — mesma regra do relatório
    // "abaixo da margem". Sem preço/custo válidos ou sem margem esperada (> 0), não alerta.
    const expectedMarginRaw = categoryProfitMargin ?? formik.values.expected_profit_margin;
    const expectedMarginNumber = Number(String(expectedMarginRaw ?? '').replace(',', '.'));
    const currentMarginRounded = saleNumberValue > 0
        ? Math.round(((saleNumberValue - costNumberValue) / saleNumberValue) * 10000) / 100
        : 0;
    const isBelowExpectedMargin = saleNumberValue > 0
        && costNumberValue > 0
        && expectedMarginNumber > 0
        && expectedMarginNumber < 100
        && currentMarginRounded < expectedMarginNumber;
    const belowExpectedMarginText = `Margem abaixo do esperado (${expectedMarginNumber}%)`;
    // Menor preço de venda (em centavos) que atinge a margem esperada — mesma fórmula do backend
    // (`SuggestedSalePriceFromCost`): margem sobre a venda, (100-m)*venda >= 100*custo.
    const adjustedSalePriceCents = (() => {
        if (!isBelowExpectedMargin) return null;
        const costCents = Math.round(costNumberValue * 100);
        const marginOf = (cents) => Math.round(((cents - costCents) / cents) * 10000) / 100;
        let cents = Math.ceil((100 * costCents) / (100 - expectedMarginNumber) - 1e-9);
        if (cents <= costCents) cents = costCents + 1;
        for (let i = 0; i < 1000 && marginOf(cents) < expectedMarginNumber; i++) cents += 1;
        return cents;
    })();
    const adjustedSalePriceText = adjustedSalePriceCents !== null
        ? formatCurrency(String(adjustedSalePriceCents))
        : '';
    const salePriceDisplay = formik.values.sale_price ? `R$ ${formik.values.sale_price}` : 'R$ 0,00';
    const quantityNumberValue = Number(formik.values.quantity) || 0;
    const minQuantityNumberValue = Number(formik.values.min_quantity) || 0;
    const isLowStock = minQuantityNumberValue > 0
        ? quantityNumberValue <= minQuantityNumberValue
        : quantityNumberValue <= 5;
    const findOptionLabel = (list, selectedId) => {
        const found = (list || []).find((item) => String(item.id) === String(selectedId));
        return found ? (found.name || found.Name || '') : '';
    };
    const selectedCategoryLabel = findOptionLabel(categories, formik.values.category);
    const selectedSupplierLabel = findOptionLabel(suppliers, formik.values.supplier);

    // Mapa campo → seção do acordeão, usado só para reabrir automaticamente a
    // seção com erro ao tentar salvar (uma seção fechada não pode "esconder"
    // um erro de validação do usuário).
    const standaloneFieldSectionMap = {
        code: 'info', name: 'info', category: 'info', subcategory: 'info', supplier: 'info', description: mobileForm ? 'market' : 'info',
        quantity: 'price', min_quantity: 'price', cost: 'price', sale_price: 'price',
    };
    const handleStandaloneSaveClick = async () => {
        const errors = await formik.validateForm();
        const errorFieldKeys = Object.keys(errors);
        if (errorFieldKeys.length > 0) {
            const sectionKeyWithError = errorFieldKeys
                .map((key) => standaloneFieldSectionMap[key])
                .find(Boolean);
            if (sectionKeyWithError) {
                ensureSectionOpen(sectionKeyWithError);
            }
        }
        formik.handleSubmit();
    };

    // Ref: prototipo-telas-insercao.html `.ff-input`/`.ffs select` (13.5px,
    // DESIGN-SYSTEM.md §5.5) — a página cheia usa esse tamanho de fonte nos
    // campos da acordeão principal, diferente do padrão global (Dropdown
    // usa `size="small"` por default nas demais telas do app).
    // Aparência do label conforme DESIGN-SYSTEM.md §5.5 "Floating label"
    // (`.ff-input`/`.ff-label` no protótipo): estado "dentro do input"
    // (sem foco/vazio) mantém texto normal 13.5px em text.disabled; estado
    // "encolhido" (foco OU já preenchido — exatamente o `.MuiInputLabel-shrink`
    // nativo do MUI) sobe para 10px, bold, uppercase, letter-spacing e cor
    // primária, em vez do shrink padrão do MUI (que não faz isso).
    // Ref: prototipo-telas-insercao.html `.ff-input`/`.ffs select` (52px de
    // altura, radius 12px, fundo surface-2, borda 1.5px) e o estado de foco
    // (borda primária fina + glow, com o fundo voltando a "surface" puro) —
    // DESIGN-SYSTEM.md §5.5. Isso substitui a caixa outlined padrão herdada
    // do tema global (que usa borda 2px sem glow no foco) só nestes campos
    // standalone; o tema global de MuiOutlinedInput continua servendo as
    // demais telas do app inalterado.
    const standaloneInputBoxSx = {
        '& .MuiOutlinedInput-root': {
            borderRadius: '12px',
            backgroundColor: 'background.subtle',
            height: '52px',
            transition: 'background-color .15s ease, box-shadow .15s ease',
            '&.MuiInputBase-multiline': {
                height: 'auto',
                minHeight: '96px',
                alignItems: 'flex-start',
                // `.ff-textarea{padding-top:24px}` do protótipo (herda os demais
                // lados de `.ff-input{padding:21px 14px 7px}`) — precisa ser fixo
                // (não depende de foco/preenchido) porque, sem o notch nativo, é
                // essa reserva de espaço no topo que evita o valor digitado
                // colidir com o label quando ele sobe para o estado encolhido.
                padding: '24px 14px 7px 14px',
            },
        },
        // `.ff-input{padding:21px 14px 7px}` do protótipo — mesma lógica acima,
        // mas para o `<input>` de linha única (o `<textarea>` é tratado à parte
        // logo acima, via padding no root, que é onde o MUI aplica o espaçamento
        // vertical de campos multiline).
        // Excluímos `.MuiSelect-select` porque o `Dropdown`
        // (labelVariant="static-micro") também usa este mesmo sx
        // (`standaloneFieldSx`) e o `<Select>` outlined do MUI renderiza o
        // valor selecionado numa `<div>` com as classes `MuiSelect-select
        // MuiOutlinedInput-input` — esse padding assimétrico (pensado só para
        // dar espaço ao label flutuante do `TextField`) empurrava o texto do
        // Select para baixo, já que o label do Select é estático, fora da
        // caixa (ver `Dropdown.js`), não precisando dessa reserva no topo.
        '& .MuiOutlinedInput-input:not(textarea):not(.MuiSelect-select)': {
            padding: '21px 14px 7px 14px',
        },
        // `.ffs select{height:52px; padding:0 14px}` do protótipo — padding
        // simétrico e centralização vertical real para o valor selecionado do
        // `<Select>` (que é uma `<div>`, sem `line-height` automático de
        // `<input>` nativo), já que aqui não há label flutuando dentro da caixa.
        '& .MuiSelect-select.MuiOutlinedInput-input': {
            padding: '0 14px',
            display: 'flex',
            alignItems: 'center',
        },
        '& .MuiOutlinedInput-notchedOutline': {
            borderWidth: '1.5px',
            borderColor: 'divider',
        },
        // O `<legend>` nativo do `<fieldset>` é o que abre o vão na borda por
        // baixo do texto do label quando ele está no estado "shrink" — esse é o
        // comportamento padrão do Material Design "outlined" (o label cruza a
        // linha da borda). O design system usa um label 100% interno à caixa
        // (ver `.ff-label`/`.ff-input:not(:placeholder-shown) ~ .ff-label` em
        // design-system.html), então a borda precisa ficar sempre fechada,
        // como um retângulo contínuo — removemos o legend para eliminar o vão.
        '& .MuiOutlinedInput-notchedOutline legend': {
            display: 'none',
        },
        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: 'primary.main',
            borderWidth: '1.5px',
        },
        '& .MuiInputBase-multiline textarea': {
            resize: 'vertical',
        },
    };

    const standaloneFieldSx = {
        '& .MuiInputBase-input': { fontSize: '13.5px' },
        // Estado normal: `top:17px` do `.ff-label` do protótipo, dentro da
        // caixa de 52px — usamos `scale(1)` (em vez do `scale(0.75)` padrão do
        // MUI) porque o tamanho de fonte já é o real (13.5px), não um valor a
        // ser escalado.
        '& .MuiInputLabel-root': {
            fontSize: '13.5px',
            color: 'text.disabled',
            transform: 'translate(14px, 17px) scale(1)',
        },
        // Estado "encolhido" (foco OU preenchido): `top:8px` do `.ff-label`
        // quando `:not(:placeholder-shown)`/`:focus` — continua DENTRO da
        // caixa (nunca em cima da borda, como seria com o `translate(14px,
        // -9px)` padrão do MUI, pensado para o notch que acabamos de remover).
        // `scale(1)` de novo aqui: o `fontSize:10px` abaixo já é o tamanho
        // final desejado: aplicar o `scale(0.75)` padrão do MUI por cima dele
        // resultaria em ~7.5px renderizados, menor que o spec.
        '& .MuiInputLabel-root.MuiInputLabel-shrink': {
            fontSize: '10px',
            fontWeight: 800,
            color: 'primary.main',
            letterSpacing: '.04em',
            textTransform: 'uppercase',
            transform: 'translate(14px, 8px) scale(1)',
        },
        ...standaloneInputBoxSx,
    };

    // `<span class="ff-req">*</span>` do protótipo — asterisco de campo
    // obrigatório na cor de erro/danger, não na cor padrão do label.
    // Usamos um nó em vez da prop `required` do MUI para não acrescentar o
    // atributo HTML `required` ao input (isso ligaria a validação nativa do
    // navegador no submit, um comportamento novo que a tarefa não pede —
    // a obrigatoriedade real já é validada pelo Yup/formik).
    const requiredFieldLabel = (text) => (
        <>
            {text} <Box component="span" sx={{ color: 'error.main' }}>*</Box>
        </>
    );

    // `.new-inline` do protótipo (DESIGN-SYSTEM.md §5.5) — link discreto
    // abaixo do campo, mesmo padrão já usado em
    // `pages/service_order/selectorClient.js` ("Novo cliente"/"Novo veículo")
    // para atalhos de cadastro rápido junto de um combobox.
    const NewInlineLink = ({ label, onClick, icon: LinkIcon = Plus }) => (
        <Box
            onClick={onClick}
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                mt: '6px',
                fontSize: '11.5px',
                fontWeight: 700,
                color: 'primary.main',
                cursor: 'pointer',
                '&:hover': { opacity: 0.75 },
            }}
        >
            <LinkIcon size={12} strokeWidth={2.5} />
            {label}
        </Box>
    );

    const formContent = (
        <>
            <Box sx={{ px: embedded ? 2 : 0, pb: embedded ? 2 : 0 }}>
                {/* Header Section - compact when embedded (sheet), full when page */}
                {embedded ? (
                    <Box
                        sx={{
                            flexShrink: 0,
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                            backgroundColor: theme.palette.background.paper,
                            pt: 3
                        }}
                    >
                        <Box sx={{ pt: 1.5, pb: 0.5, display: 'flex', justifyContent: 'center' }}>
                            <Box
                                sx={{
                                    width: 40,
                                    height: 4,
                                    borderRadius: 2,
                                    backgroundColor: theme.palette.mode === 'dark'
                                        ? 'rgba(255,255,255,0.2)'
                                        : 'rgba(0,0,0,0.12)'
                                }}
                            />
                        </Box>
                        <Box
                            sx={{
                                px: 2,
                                pt: 5,
                                pb: 2,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 2
                            }}
                        >
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography variant="h6" sx={{ fontWeight: 600 }} noWrap>
                                    {id ? 'Editar Produto' : 'Novo Produto'}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" noWrap>
                                    {id ? 'Atualize as informações do produto' : 'Preencha as informações do produto'}
                                </Typography>
                            </Box>
                            <IconButton
                                onClick={() => onClose && onClose()}
                                size="medium"
                                sx={{
                                    color: 'text.secondary',
                                    '&:hover': { backgroundColor: theme.palette.action.hover }
                                }}
                                aria-label="Fechar"
                            >
                                <CloseIcon />
                            </IconButton>
                        </Box>
                    </Box>
                ) : (mobileForm ? null : (
                    <Box sx={{ mb: 2.5 }}>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: '20px',
                                flexWrap: 'wrap',
                                mb: 2.25,
                            }}
                        >
                            <Box>
                                <Typography sx={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.02em', mb: 0.5 }}>
                                    {id ? 'Editar produto' : 'Novo produto'}
                                </Typography>
                                <Typography sx={{ fontSize: '13.5px', color: 'text.secondary' }}>
                                    {id
                                        ? 'Atualize as seções do produto — o card ao lado mostra como ele aparece na venda.'
                                        : 'Cadastre por seções — o card ao lado é exatamente como aparece na tela de venda.'}
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '14px', flexShrink: 0 }}>
                                <Box sx={{ position: 'relative', width: 54, height: 54, flexShrink: 0 }}>
                                    <svg viewBox="0 0 54 54" style={{ transform: 'rotate(-90deg)', width: '100%', height: '100%' }}>
                                        <circle cx="27" cy="27" r={RING_R} fill="none" strokeWidth="5" stroke={theme.palette.divider} />
                                        <circle
                                            cx="27"
                                            cy="27"
                                            r={RING_R}
                                            fill="none"
                                            strokeWidth="5"
                                            strokeLinecap="round"
                                            stroke={theme.palette.primary.main}
                                            strokeDasharray={RING_CIRC}
                                            strokeDashoffset={ringOffset}
                                            style={{ transition: 'stroke-dashoffset .35s ease' }}
                                        />
                                    </svg>
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            inset: 0,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: 12,
                                            fontWeight: 800,
                                        }}
                                    >
                                        {progressPct}%
                                    </Box>
                                </Box>
                                <Box>
                                    <Typography sx={{ fontSize: 13, fontWeight: 700 }}>Cadastro</Typography>
                                    <Typography sx={{ fontSize: '11.5px', color: 'text.disabled' }}>
                                        completo conforme você preenche
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>

                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            {standaloneSections.map((sec, idx) => {
                                const isSecOpen = isSectionOpen(sec.key);
                                const isSecComplete = sec.key === 'media'
                                    ? hasProductImage
                                    : sec.requiredFields.length > 0 && sec.requiredFields.every(isFieldFilled);
                                return (
                                    <Box
                                        key={sec.key}
                                        onClick={() => ensureSectionOpen(sec.key)}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '7px',
                                            px: '14px',
                                            py: '8px',
                                            borderRadius: '24px',
                                            border: '1.5px solid',
                                            borderColor: isSecOpen ? 'primary.main' : 'divider',
                                            backgroundColor: isSecOpen ? 'info.bg' : 'background.paper',
                                            color: isSecOpen ? 'primary.main' : 'text.secondary',
                                            fontSize: '12px',
                                            fontWeight: 700,
                                            cursor: 'pointer',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: 17,
                                                height: 17,
                                                borderRadius: '50%',
                                                border: '2px solid',
                                                borderColor: isSecComplete ? 'success.main' : (isSecOpen ? 'primary.main' : 'text.disabled'),
                                                backgroundColor: isSecComplete ? 'success.main' : 'transparent',
                                                color: isSecComplete ? '#fff' : 'transparent',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontSize: 9,
                                                flexShrink: 0,
                                            }}
                                        >
                                            {isSecComplete ? <Check size={10} strokeWidth={3} /> : idx + 1}
                                        </Box>
                                        {sec.title}
                                    </Box>
                                );
                            })}
                        </Box>
                    </Box>
                ))}

                {/* Main Form (modo embedded — layout preexistente do drawer da Lista de produtos) */}
                {embedded && (
                <Paper
                    elevation={embedded ? 0 : 2}
                    sx={{
                        borderRadius: embedded ? 2 : 3,
                        overflow: 'hidden',
                        border: embedded ? '1px solid' : '1px solid',
                        borderColor: theme.palette.divider,
                        backgroundColor: theme.palette.background.paper,
                        boxShadow: embedded ? 'none' : (theme.palette.mode === 'dark'
                            ? '0 4px 20px rgba(0,0,0,0.3)'
                            : '0 4px 20px rgba(0,0,0,0.08)'),
                        ...(embedded && { mb: 0 })
                    }}
                >
                    <form onSubmit={formik.handleSubmit}>
                        {/* Categorization Section */}
                        <Box sx={{
                            p: 2,
                            backgroundColor: theme.palette.mode === 'dark'
                                ? 'rgba(255,255,255,0.02)'
                                : 'grey.50'
                        }}>
                            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
                                <CategoryIcon color="primary" />
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    Categorização
                                </Typography>
                            </Stack>

                            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 3 }}>
                                <Box sx={{ flex: 1 }}>
                                    {isLoadingInput ? (
                                        <Skeleton variant="text" width="100%" height={56} />
                                    ) : (
                                        <Dropdown
                                            label="Fornecedor"
                                            labelVariant="static-micro"
                                            data={suppliers}
                                            selectedItem={formik.values.supplier}
                                            handleChange={handleChangeSupplier}
                                            error={formik.touched.supplier && Boolean(formik.errors.supplier)}
                                            helperText={formik.touched.supplier && formik.errors.supplier}
                                            variant="outlined"
                                            size="medium"
                                            sx={standaloneFieldSx}
                                        />
                                    )}
                                </Box>

                                <Box sx={{ flex: 1 }}>
                                    {isLoadingInput ? (
                                        <Skeleton variant="text" width="100%" height={56} />
                                    ) : (
                                        <>
                                            <Dropdown
                                                label={requiredFieldLabel('Categoria')}
                                                labelVariant="static-micro"
                                                data={categories}
                                                selectedItem={formik.values.category}
                                                handleChange={handleChangeCategory}
                                                error={formik.touched.category && Boolean(formik.errors.category)}
                                                helperText={formik.touched.category && formik.errors.category}
                                                variant="outlined"
                                                size="medium"
                                                sx={standaloneFieldSx}
                                            />
                                            <NewInlineLink label="Nova categoria" onClick={handleNewCategory} />
                                        </>
                                    )}
                                </Box>

                                <Box sx={{ flex: 1 }}>
                                    {isLoadingInput ? (
                                        <Skeleton variant="text" width="100%" height={56} />
                                    ) : (
                                        <>
                                            <Dropdown
                                                label="Subcategoria"
                                                labelVariant="static-micro"
                                                data={subCategories}
                                                selectedItem={formik.values.subcategory}
                                                handleChange={handleChangeSubCategory}
                                                variant="outlined"
                                                size="medium"
                                                sx={standaloneFieldSx}
                                            />
                                            <NewInlineLink label="Nova subcategoria" onClick={handleNewSubCategory} />
                                        </>
                                    )}
                                </Box>
                            </Box>
                        </Box>

                        <Divider />

                        {/* Image and Basic Info Section */}
                        <Box sx={{
                            p: 2,
                            backgroundColor: theme.palette.mode === 'dark'
                                ? 'rgba(255,255,255,0.02)'
                                : 'grey.50'
                        }}>
                            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
                                <PhotoIcon color="primary" />
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    Imagem e Informações Básicas
                                </Typography>
                            </Stack>

                            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
                                {/* Image Upload */}
                                <Box sx={{ width: { xs: '100%', md: '20%' }, minWidth: { md: 160 } }}>
                                    <Box sx={{ position: 'relative' }}>
                                        {isLoadingInput ? (
                                            <Skeleton variant="rectangular" width="100%" height={140} sx={{ borderRadius: 2 }} />
                                        ) : (
                                            <Box
                                                onDragEnter={handleDrag}
                                                onDragLeave={handleDrag}
                                                onDragOver={handleDrag}
                                                onDrop={handleDrop}
                                                sx={{
                                                    width: '100%',
                                                    height: 140,
                                                    border: dragActive
                                                        ? `2px dashed ${theme.palette.primary.main}`
                                                        : theme.palette.mode === 'dark'
                                                            ? '1px dashed rgba(255,255,255,0.3)'
                                                            : '1px dashed #e0e0e0',
                                                    borderRadius: 2,
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    justifyContent: 'center',
                                                    alignItems: 'center',
                                                    overflow: 'hidden',
                                                    backgroundColor: dragActive
                                                        ? theme.palette.mode === 'dark'
                                                            ? 'rgba(25, 118, 210, 0.1)'
                                                            : 'rgba(25, 118, 210, 0.04)'
                                                        : theme.palette.mode === 'dark'
                                                            ? 'rgba(255,255,255,0.02)'
                                                            : 'transparent',
                                                    transition: 'all 0.3s ease',
                                                    cursor: 'pointer',
                                                    '&:hover': {
                                                        borderColor: theme.palette.primary.main,
                                                        backgroundColor: theme.palette.mode === 'dark'
                                                            ? 'rgba(25, 118, 210, 0.1)'
                                                            : 'rgba(25, 118, 210, 0.04)'
                                                    }
                                                }}
                                                onClick={() => document.getElementById('image-upload').click()}
                                            >
                                                {previewImage || formik.values.image ? (
                                                    <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
                                                        <img
                                                            src={previewImage || formik.values.image}
                                                            alt="Product"
                                                            style={{
                                                                width: '100%',
                                                                height: '100%',
                                                                objectFit: 'cover',
                                                                borderRadius: 8
                                                            }}
                                                        />
                                                        <IconButton
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                removeImage();
                                                            }}
                                                            sx={{
                                                                position: 'absolute',
                                                                top: 8,
                                                                right: 8,
                                                                backgroundColor: 'rgba(0,0,0,0.5)',
                                                                color: 'white',
                                                                '&:hover': { backgroundColor: 'rgba(0,0,0,0.7)' }
                                                            }}
                                                            size="small"
                                                        >
                                                            <DeleteIcon color="error" />
                                                        </IconButton>
                                                    </Box>
                                                ) : (
                                                    <Box sx={{ textAlign: 'center', px: 0.5 }}>
                                                        <AddPhotoAlternateIcon sx={{
                                                            fontSize: 36,
                                                            color: theme.palette.mode === 'dark' ? 'grey.500' : 'grey.400',
                                                            mb: 0.5
                                                        }} />
                                                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                                                            Clique ou arraste
                                                        </Typography>
                                                    </Box>
                                                )}
                                            </Box>
                                        )}

                                        <input
                                            id="image-upload"
                                            type="file"
                                            hidden
                                            accept="image/*"
                                            onChange={(event) => {
                                                const file = event.target.files[0];
                                                if (file) {
                                                    handleImageUpload(file);
                                                }
                                            }}
                                        />
                                    </Box>
                                </Box>

                                {/* Basic Info */}
                                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                                        <Box sx={{ flex: 1 }}>
                                            {isLoadingInput ? (
                                                <Skeleton variant="text" width="100%" height={56} />
                                            ) : (
                                                <TextField
                                                    autoFocus
                                                    label={requiredFieldLabel('Código do Produto')}
                                                    fullWidth
                                                    name="code"
                                                    value={formik.values.code}
                                                    onChange={formik.handleChange}
                                                    onBlur={async (value) => {

                                                        const response = await httpClient.get(`/stock/productId?code=${value.target.value}`);
                                                        if (response.data.id !== '') {
                                                            formik.setErrors({ code: 'Código já existe' });
                                                            return false;
                                                        }

                                                        return true;
                                                    }}
                                                    InputProps={{
                                                        endAdornment: (
                                                            <InputAdornment position="end">
                                                                <Tooltip title="Ler código de barras">
                                                                    <IconButton
                                                                        onClick={() => setScannerOpen(true)}
                                                                        aria-label="Ler código de barras"
                                                                        sx={{ width: 44, height: 44 }}
                                                                    >
                                                                        <QrCodeScannerIcon />
                                                                    </IconButton>
                                                                </Tooltip>
                                                                <Tooltip title="Sugerir código">
                                                                    <IconButton
                                                                        onClick={handleSuggestCode}
                                                                        size="small"
                                                                        aria-label="Sugerir código do produto"
                                                                    >
                                                                        <AutoFixHighIcon />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                    error={formik.touched.code && Boolean(formik.errors.code)}
                                                    helperText={formik.touched.code && formik.errors.code}
                                                    variant="outlined"
                                                    sx={standaloneFieldSx}
                                                />
                                            )}
                                        </Box>

                                        <Box sx={{ flex: 1 }}>
                                            {isLoadingInput ? (
                                                <Skeleton variant="text" width="100%" height={56} />
                                            ) : (
                                                <TextField
                                                    label={requiredFieldLabel('Nome do Produto')}
                                                    fullWidth
                                                    name="name"
                                                    value={formik.values.name}
                                                    onChange={formik.handleChange}
                                                    error={formik.touched.name && Boolean(formik.errors.name)}
                                                    helperText={formik.touched.name && formik.errors.name}
                                                    variant="outlined"
                                                    sx={standaloneFieldSx}
                                                />
                                            )}
                                        </Box>
                                    </Box>

                                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                                        <Box sx={{ flex: 1 }}>
                                            {isLoadingInput ? (
                                                <Skeleton variant="text" width="100%" height={56} />
                                            ) : (
                                                <TextField
                                                    disabled={formik.values.is_generic || id}
                                                    label={requiredFieldLabel('Quantidade')}
                                                    fullWidth
                                                    name="quantity"
                                                    type="number"
                                                    value={formik.values.quantity}
                                                    onChange={formik.handleChange}
                                                    error={formik.touched.quantity && Boolean(formik.errors.quantity)}
                                                    helperText={formik.touched.quantity && formik.errors.quantity}
                                                    variant="outlined"
                                                    inputProps={{ min: 0 }}
                                                    sx={standaloneFieldSx}
                                                />
                                            )}
                                        </Box>

                                        <Box sx={{ flex: 1 }}>
                                            {isLoadingInput ? (
                                                <Skeleton variant="text" width="100%" height={56} />
                                            ) : (
                                                <TextField
                                                    disabled={formik.values.is_generic}
                                                    label={requiredFieldLabel('Quantidade Mínima')}
                                                    fullWidth
                                                    name="min_quantity"
                                                    type="number"
                                                    value={formik.values.min_quantity}
                                                    onChange={formik.handleChange}
                                                    error={formik.touched.min_quantity && Boolean(formik.errors.min_quantity)}
                                                    helperText={formik.touched.min_quantity && formik.errors.min_quantity}
                                                    variant="outlined"
                                                    inputProps={{ min: 0 }}
                                                    sx={standaloneFieldSx}
                                                />
                                            )}
                                        </Box>
                                    </Box>

                                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                                        <Box sx={{ flex: 1 }}>
                                            {isLoadingInput ? (
                                                <Skeleton variant="text" width="100%" height={56} />
                                            ) : (
                                                <TextField
                                                    label="Valor do Custo"
                                                    fullWidth
                                                    name="cost"
                                                    type="text"
                                                    value={formik.values.cost}
                                                    onChange={handleChangeCost}
                                                    onBlur={handleBlurCost}
                                                    error={formik.touched.cost && Boolean(formik.errors.cost)}
                                                    helperText={formik.touched.cost && formik.errors.cost}
                                                    variant="outlined"
                                                    sx={standaloneFieldSx}
                                                />
                                            )}
                                        </Box>

                                        <Box sx={{ flex: 1 }}>
                                            {isLoadingInput ? (
                                                <Skeleton variant="text" width="100%" height={56} />
                                            ) : (
                                                <TextField
                                                    label={requiredFieldLabel('Valor de Venda')}
                                                    fullWidth
                                                    name="sale_price"
                                                    type="text"
                                                    value={formik.values.sale_price}
                                                    onChange={(e) => {
                                                        const value = formatCurrency(e.target.value);
                                                        formik.setFieldValue('sale_price', value);
                                                    }}
                                                    error={formik.touched.sale_price && Boolean(formik.errors.sale_price)}
                                                    helperText={formik.touched.sale_price && formik.errors.sale_price}
                                                    variant="outlined"
                                                    sx={standaloneFieldSx}
                                                    InputProps={{
                                                        endAdornment: (
                                                            <InputAdornment position="end">
                                                                <Tooltip title="Calcular Preço de Venda">
                                                                    <IconButton
                                                                        onClick={() => setOpenDialogCalculateSalePrice(true)}
                                                                        size="small"
                                                                        sx={{ backgroundColor: 'primary.main', color: '#fff', '&:hover': { backgroundColor: 'primary.dark' } }}
                                                                    >
                                                                        <CalculateIcon sx={{ fontSize: 16 }} />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                />
                                            )}
                                        </Box>
                                    </Box>
                                </Box>
                            </Box>
                        </Box>

                        <Divider />

                        {/* Configuration Section */}
                        <Box sx={{ p: 2 }}>
                            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
                                <InventoryIcon color="primary" />
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    Configurações do Produto
                                </Typography>
                            </Stack>

                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={formik.values.is_generic}
                                        onChange={(e) => {
                                            if (e.target.checked) {
                                                formik.setFieldValue('quantity', 1);
                                                formik.setFieldValue('min_quantity', 1);
                                                formik.setFieldValue('cost', '0,00');
                                                formik.setFieldValue('sale_price', '0,00');
                                            } else {
                                                formik.setFieldValue('cost', '');
                                                formik.setFieldValue('sale_price', '');
                                            }
                                            formik.setFieldValue('is_generic', e.target.checked);
                                        }}
                                        disabled={id}
                                        size="medium"
                                    />
                                }
                                label={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Chip
                                            label="Produto Genérico"
                                            size="small"
                                            color={formik.values.is_generic ? "primary" : "default"}
                                            variant={formik.values.is_generic ? "filled" : "outlined"}
                                        />
                                        {formik.values.is_generic && (
                                            <Alert severity="info" sx={{ py: 0, px: 1, fontSize: '0.75rem' }}>
                                                Sem controle de estoque
                                            </Alert>
                                        )}
                                    </Box>
                                }
                            />

                            <Box sx={{ mt: 2 }}>
                                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                                    <BusinessIcon color="primary" />
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                        Marketplace
                                    </Typography>
                                    <Tooltip
                                        title="Ao marcar as opções, o produto será sincronizado automaticamente com o marketplace (Mercado Livre ou outro que estiver marcado). Isso criara um anúncio, editará as informações e atualizará o estoque."
                                    >
                                        <InfoIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                                    </Tooltip>
                                </Stack>

                                {!isEditing && (
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={formik.values.mercado_livre_create}
                                                onChange={handleMarketplaceToggleChange}
                                                size="medium"
                                            />
                                        }
                                        label={mercadoLivreLabel}
                                    />
                                )}

                                {isEditing && !hasMercadoLivreListing && (
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={formik.values.mercado_livre_create}
                                                onChange={handleMarketplaceToggleChange}
                                                size="medium"
                                            />
                                        }
                                        label={mercadoLivreLabel}
                                    />
                                )}

                                

                                
                            </Box>
                        </Box>

                        <Divider />

                        {/* Description Section */}
                        <Box sx={{
                            p: 2,
                            backgroundColor: theme.palette.mode === 'dark'
                                ? 'rgba(255,255,255,0.02)'
                                : 'grey.50'
                        }}>
                            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                                <DescriptionIcon color="primary" />
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    Descrição
                                </Typography>
                            </Stack>
                            {isLoadingInput ? (
                                <Skeleton variant="rectangular" width="100%" height={80} sx={{ borderRadius: 1 }} />
                            ) : (
                                <Box>
                                    <TextField
                                        fullWidth
                                        multiline
                                        minRows={3}
                                        label="Descrição do Produto (opcional)"
                                        name="description"
                                        value={formik.values.description}
                                        onChange={formik.handleChange}
                                        error={formik.touched.description && Boolean(formik.errors.description)}
                                        helperText={formik.touched.description && formik.errors.description}
                                        inputProps={{ maxLength: 100 }}
                                        variant="outlined"
                                        sx={standaloneFieldSx}
                                    />
                                    <Typography sx={{ fontSize: 11, color: 'text.disabled', mt: 0.5, textAlign: 'right' }}>
                                        {(formik.values.description || '').length}/100
                                    </Typography>
                                </Box>
                            )}
                        </Box>

                        {/* Action Buttons */}
                        <Box sx={{
                            p: embedded ? 2 : 2,
                            backgroundColor: theme.palette.background.paper,
                            borderTop: '1px solid',
                            borderColor: theme.palette.divider,
                            ...(embedded && {
                                position: 'sticky',
                                bottom: 0,
                                zIndex: 1,
                                boxShadow: theme.palette.mode === 'dark'
                                    ? '0 -4px 12px rgba(0,0,0,0.2)'
                                    : '0 -4px 12px rgba(0,0,0,0.06)'
                            })
                        }}>
                            <Stack direction="row" spacing={2} justifyContent="flex-end">
                                <Button
                                    variant="outlined"
                                    onClick={() => (embedded && onClose ? onClose() : goToList())}
                                >
                                    Fechar
                                </Button>
                                <Button
                                    variant="contained"
                                    onClick={formik.handleSubmit}
                                    disabled={loading}
                                >
                                    {loading ? 'Salvando...' : (id ? 'Atualizar' : 'Salvar')}
                                </Button>
                            </Stack>
                        </Box>
                    </form>
                </Paper>
                )}

                {/* Main Form (modo página cheia — acordeão + preview ao vivo, DESIGN-SYSTEM.md §5.5/§6.2) */}
                {mobileForm && (
                <form onSubmit={formik.handleSubmit} noValidate>
                    {isLoadingInput ? (
                        <Stack spacing={1.25}>
                            {[1, 2, 3].map((n) => <Skeleton key={n} variant="rounded" height={64} sx={{ borderRadius: '16px' }} />)}
                        </Stack>
                    ) : (
                        <MobileProductForm
                            formik={formik}
                            isEditing={isEditing}
                            categories={categories}
                            subCategories={subCategories}
                            suppliers={suppliers}
                            onChangeCategory={handleChangeCategory}
                            onChangeSubCategory={handleChangeSubCategory}
                            onChangeSupplier={handleChangeSupplier}
                            onNewCategory={handleNewCategory}
                            onNewSubCategory={handleNewSubCategory}
                            onCodeBlur={async (event) => {
                                const response = await httpClient.get(`/stock/productId?code=${event.target.value}`);
                                if (response.data.id !== '') {
                                    formik.setErrors({ code: 'Código já existe' });
                                    return false;
                                }
                                return true;
                            }}
                            onScan={() => setScannerOpen(true)}
                            onSuggestCode={handleSuggestCode}
                            onChangeCost={handleChangeCost}
                            onBlurCost={handleBlurCost}
                            onChangeSalePrice={(e) => formik.setFieldValue('sale_price', formatCurrency(e.target.value))}
                            onOpenCalculator={() => setOpenDialogCalculateSalePrice(true)}
                            marginValue={saleNumberValue > 0 ? marginPct.toFixed(1) : '—'}
                            marginHelp={saleNumberValue > 0
                                ? `Margem calculada: ${marginLabel}${expectedMarginNumber > 0 && expectedMarginNumber < 100 && costNumberValue > 0
                                    ? (isBelowExpectedMargin ? ' · abaixo do esperado' : ' · acima do esperado')
                                    : ''}`
                                : undefined}
                            belowExpected={isBelowExpectedMargin}
                            adjustedSalePriceText={adjustedSalePriceText}
                            expectedMargin={expectedMarginNumber}
                            onAdjustPrice={() => formik.setFieldValue('sale_price', adjustedSalePriceText)}
                            onToggleGeneric={(checked) => {
                                if (checked) {
                                    formik.setFieldValue('quantity', 1);
                                    formik.setFieldValue('min_quantity', 1);
                                    formik.setFieldValue('cost', '0,00');
                                    formik.setFieldValue('sale_price', '0,00');
                                } else {
                                    formik.setFieldValue('cost', '');
                                    formik.setFieldValue('sale_price', '');
                                }
                                formik.setFieldValue('is_generic', checked);
                            }}
                            showMarketplace={hasModule('marketplace') && (!isEditing || !hasMercadoLivreListing)}
                            marketplaceChecked={Boolean(formik.values.mercado_livre_create)}
                            marketplaceDisabled={isMarketplaceSwitchDisabled}
                            marketplaceUrl={mercadoLivreUrl}
                            onToggleMarketplace={handleMarketplaceToggleChange}
                            previewImage={previewImage}
                            onPickImage={handleImageUpload}
                            onRemoveImage={removeImage}
                            isSectionOpen={isSectionOpen}
                            onToggleSection={toggleSection}
                            loading={loading}
                            onCancel={goToList}
                            onSave={handleStandaloneSaveClick}
                        />
                    )}
                </form>
                )}

                {!embedded && !mobileForm && (
                <form onSubmit={formik.handleSubmit}>
                    {isLoadingInput ? (
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 340px' }, gap: '20px' }}>
                            <Stack spacing={1.75}>
                                <Skeleton variant="rounded" height={220} sx={{ borderRadius: '18px' }} />
                                <Skeleton variant="rounded" height={220} sx={{ borderRadius: '18px' }} />
                                <Skeleton variant="rounded" height={180} sx={{ borderRadius: '18px' }} />
                            </Stack>
                            <Skeleton variant="rounded" height={420} sx={{ borderRadius: '20px' }} />
                        </Box>
                    ) : (
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 340px' }, gap: '20px', alignItems: 'start', '& > *': { minWidth: 0 } }}>
                            <Stack spacing={1.75} sx={{ minWidth: 0 }}>
                                <AccordionSection
                                    open={isSectionOpen('info')}
                                    complete={standaloneSections[0].requiredFields.every(isFieldFilled)}
                                    icon={PackageIcon}
                                    title="Informações básicas"
                                    subtitle="Nome, código, categoria e fornecedor"
                                    onToggle={() => toggleSection('info')}
                                >
                                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2, mb: 2, '& > *': { minWidth: 0 } }}>
                                        {/* Envolvido num `Box` (mesmo padrão de Categoria/Subcategoria
                                            logo abaixo) para que o item da grid seja esse `Box`, não a raiz
                                            interna do `Dropdown` — que usa `alignItems:'flex-end'` (ver
                                            `component/dropdown/Dropdown.js`, variante `static-micro`).
                                            Sem esse wrapper, o `align-items:stretch` padrão da grid esticava
                                            a raiz do próprio `Dropdown` para igualar a altura da linha
                                            (mais alta por causa do link "+ Nova categoria/subcategoria"
                                            abaixo dos outros dois campos), e o `flex-end` empurrava o campo
                                            "Fornecedor" ~28px para baixo, desalinhando o topo dos 3 campos. */}
                                        <Box sx={{ minWidth: 0 }}>
                                            <Dropdown
                                                label="Fornecedor"
                                                labelVariant="static-micro"
                                                data={suppliers}
                                                selectedItem={formik.values.supplier}
                                                handleChange={handleChangeSupplier}
                                                error={formik.touched.supplier && Boolean(formik.errors.supplier)}
                                                helperText={formik.touched.supplier && formik.errors.supplier}
                                                variant="outlined"
                                                size="medium"
                                                sx={standaloneFieldSx}
                                            />
                                        </Box>
                                        <Box sx={{ minWidth: 0 }}>
                                            <Dropdown
                                                label={requiredFieldLabel('Categoria')}
                                                labelVariant="static-micro"
                                                data={categories}
                                                selectedItem={formik.values.category}
                                                handleChange={handleChangeCategory}
                                                error={formik.touched.category && Boolean(formik.errors.category)}
                                                helperText={formik.touched.category && formik.errors.category}
                                                variant="outlined"
                                                size="medium"
                                                sx={standaloneFieldSx}
                                            />
                                            <NewInlineLink label="Nova categoria" onClick={handleNewCategory} />
                                        </Box>
                                        <Box sx={{ minWidth: 0 }}>
                                            <Dropdown
                                                label="Subcategoria"
                                                labelVariant="static-micro"
                                                data={subCategories}
                                                selectedItem={formik.values.subcategory}
                                                handleChange={handleChangeSubCategory}
                                                variant="outlined"
                                                size="medium"
                                                sx={standaloneFieldSx}
                                            />
                                            <NewInlineLink label="Nova subcategoria" onClick={handleNewSubCategory} />
                                        </Box>
                                    </Box>
                                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 2, '& > *': { minWidth: 0 } }}>
                                        <TextField
                                            autoFocus
                                            label={requiredFieldLabel('Código do Produto')}
                                            fullWidth
                                            name="code"
                                            value={formik.values.code}
                                            onChange={formik.handleChange}
                                            onBlur={async (value) => {
                                                const response = await httpClient.get(`/stock/productId?code=${value.target.value}`);
                                                if (response.data.id !== '') {
                                                    formik.setErrors({ code: 'Código já existe' });
                                                    return false;
                                                }
                                                return true;
                                            }}
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <Tooltip title="Ler código de barras">
                                                            <IconButton onClick={() => setScannerOpen(true)} aria-label="Ler código de barras" sx={{ width: 44, height: 44 }}>
                                                                <QrCodeScannerIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Sugerir código">
                                                            <IconButton onClick={handleSuggestCode} size="small" aria-label="Sugerir código do produto">
                                                                <Wand2 size={16} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </InputAdornment>
                                                ),
                                            }}
                                            error={formik.touched.code && Boolean(formik.errors.code)}
                                            helperText={formik.touched.code && formik.errors.code}
                                            variant="outlined"
                                            sx={standaloneFieldSx}
                                        />
                                        <TextField
                                            label={requiredFieldLabel('Nome do Produto')}
                                            fullWidth
                                            name="name"
                                            value={formik.values.name}
                                            onChange={formik.handleChange}
                                            error={formik.touched.name && Boolean(formik.errors.name)}
                                            helperText={formik.touched.name && formik.errors.name}
                                            variant="outlined"
                                            sx={standaloneFieldSx}
                                        />
                                    </Box>
                                    <Box>
                                        <TextField
                                            fullWidth
                                            multiline
                                            minRows={3}
                                            label="Descrição do Produto (opcional)"
                                            name="description"
                                            value={formik.values.description}
                                            onChange={formik.handleChange}
                                            error={formik.touched.description && Boolean(formik.errors.description)}
                                            helperText={formik.touched.description && formik.errors.description}
                                            inputProps={{ maxLength: 100 }}
                                            variant="outlined"
                                            sx={standaloneFieldSx}
                                        />
                                        <Typography sx={{ fontSize: 11, color: 'text.disabled', mt: 0.5, textAlign: 'right' }}>
                                            {(formik.values.description || '').length}/100
                                        </Typography>
                                    </Box>
                                </AccordionSection>

                                <AccordionSection
                                    open={isSectionOpen('price')}
                                    complete={standaloneSections[1].requiredFields.every(isFieldFilled)}
                                    icon={PriceIcon}
                                    title="Preço e estoque"
                                    subtitle="Custo, margem e quantidade disponível"
                                    onToggle={() => toggleSection('price')}
                                >
                                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 2, '& > *': { minWidth: 0 } }}>
                                        <TextField
                                            disabled={formik.values.is_generic || Boolean(id)}
                                            label={requiredFieldLabel('Quantidade')}
                                            fullWidth
                                            name="quantity"
                                            type="number"
                                            value={formik.values.quantity}
                                            onChange={formik.handleChange}
                                            error={formik.touched.quantity && Boolean(formik.errors.quantity)}
                                            helperText={formik.touched.quantity && formik.errors.quantity}
                                            inputProps={{ min: 0 }}
                                            variant="outlined"
                                            sx={standaloneFieldSx}
                                        />
                                        <TextField
                                            disabled={formik.values.is_generic}
                                            label={requiredFieldLabel('Quantidade Mínima')}
                                            fullWidth
                                            name="min_quantity"
                                            type="number"
                                            value={formik.values.min_quantity}
                                            onChange={formik.handleChange}
                                            error={formik.touched.min_quantity && Boolean(formik.errors.min_quantity)}
                                            helperText={formik.touched.min_quantity && formik.errors.min_quantity}
                                            inputProps={{ min: 0 }}
                                            variant="outlined"
                                            sx={standaloneFieldSx}
                                        />
                                    </Box>
                                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2, '& > *': { minWidth: 0 } }}>
                                        <TextField
                                            label="Valor do Custo"
                                            fullWidth
                                            name="cost"
                                            type="text"
                                            value={formik.values.cost}
                                            onChange={handleChangeCost}
                                            onBlur={handleBlurCost}
                                            error={formik.touched.cost && Boolean(formik.errors.cost)}
                                            helperText={formik.touched.cost && formik.errors.cost}
                                            variant="outlined"
                                            sx={standaloneFieldSx}
                                        />
                                        <Box>
                                            <TextField
                                                label={requiredFieldLabel('Valor de Venda')}
                                                fullWidth
                                                name="sale_price"
                                                type="text"
                                                value={formik.values.sale_price}
                                                onChange={(e) => {
                                                    const value = formatCurrency(e.target.value);
                                                    formik.setFieldValue('sale_price', value);
                                                }}
                                                error={formik.touched.sale_price && Boolean(formik.errors.sale_price)}
                                                helperText={formik.touched.sale_price && formik.errors.sale_price}
                                                variant="outlined"
                                                sx={standaloneFieldSx}
                                                InputProps={{
                                                    endAdornment: (
                                                        <InputAdornment position="end">
                                                            <Tooltip title="Calcular Preço de Venda">
                                                                <IconButton
                                                                    onClick={() => setOpenDialogCalculateSalePrice(true)}
                                                                    size="small"
                                                                    sx={{ backgroundColor: 'primary.main', color: '#fff', '&:hover': { backgroundColor: 'primary.dark' } }}
                                                                >
                                                                    <CalculatorLucideIcon size={15} />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </InputAdornment>
                                                    ),
                                                }}
                                            />
                                            {isBelowExpectedMargin && (
                                                <Link
                                                    component="button"
                                                    type="button"
                                                    variant="caption"
                                                    underline="always"
                                                    onClick={() => formik.setFieldValue('sale_price', adjustedSalePriceText)}
                                                    sx={{ mt: 0.5, ml: 0.5, textAlign: 'left', fontWeight: 600, color: 'warning.dark' }}
                                                >
                                                    {`Ajustar para R$ ${adjustedSalePriceText} (margem de ${expectedMarginNumber}%)`}
                                                </Link>
                                            )}
                                        </Box>
                                        <TextField
                                            label="Margem (calculada)"
                                            fullWidth
                                            value={marginLabel}
                                            disabled
                                            variant="outlined"
                                            sx={standaloneFieldSx}
                                            InputProps={isBelowExpectedMargin ? {
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <Tooltip title={belowExpectedMarginText}>
                                                            <Box component="span" sx={{ display: 'inline-flex', color: 'warning.main' }}>
                                                                <AlertTriangle size={16} strokeWidth={2} />
                                                            </Box>
                                                        </Tooltip>
                                                    </InputAdornment>
                                                ),
                                            } : undefined}
                                        />
                                    </Box>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            gap: '14px',
                                            pt: '14px',
                                            mt: 2,
                                            borderTop: '1px solid',
                                            borderColor: 'divider',
                                        }}
                                    >
                                        <Box>
                                            <Typography sx={{ fontSize: 13, fontWeight: 700 }}>Produto genérico</Typography>
                                            <Typography sx={{ fontSize: '11.5px', color: 'text.disabled' }}>
                                                {formik.values.is_generic
                                                    ? 'Sem controle de estoque'
                                                    : 'Ative para itens sem controle de estoque'}
                                            </Typography>
                                        </Box>
                                        <Switch
                                            checked={formik.values.is_generic}
                                            disabled={Boolean(id)}
                                            onChange={(e) => {
                                                if (e.target.checked) {
                                                    formik.setFieldValue('quantity', 1);
                                                    formik.setFieldValue('min_quantity', 1);
                                                    formik.setFieldValue('cost', '0,00');
                                                    formik.setFieldValue('sale_price', '0,00');
                                                } else {
                                                    formik.setFieldValue('cost', '');
                                                    formik.setFieldValue('sale_price', '');
                                                }
                                                formik.setFieldValue('is_generic', e.target.checked);
                                            }}
                                        />
                                    </Box>
                                </AccordionSection>

                                <AccordionSection
                                    open={isSectionOpen('media')}
                                    complete={hasProductImage}
                                    icon={ImageSectionIcon}
                                    title="Imagem do produto"
                                    subtitle="Foto exibida no sistema e nas vendas"
                                    onToggle={() => toggleSection('media')}
                                >
                                    <Box
                                        onDragEnter={handleDrag}
                                        onDragLeave={handleDrag}
                                        onDragOver={handleDrag}
                                        onDrop={handleDrop}
                                        onClick={() => document.getElementById('image-upload-standalone').click()}
                                        sx={{
                                            border: '2px dashed',
                                            borderColor: dragActive ? 'primary.main' : 'divider',
                                            borderRadius: '16px',
                                            textAlign: 'center',
                                            cursor: 'pointer',
                                            backgroundColor: dragActive ? 'info.bg' : 'background.subtle',
                                            overflow: 'hidden',
                                            position: 'relative',
                                            width: '100%',
                                            maxWidth: 260,
                                            aspectRatio: '1 / 1',
                                            mx: 'auto',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            p: previewImage || formik.values.image ? 0 : '16px',
                                            '&:hover': { borderColor: 'primary.main' },
                                        }}
                                    >
                                        {previewImage || formik.values.image ? (
                                            <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
                                                <img
                                                    src={previewImage || formik.values.image}
                                                    alt="Produto"
                                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                />
                                                <IconButton
                                                    onClick={(e) => { e.stopPropagation(); removeImage(); }}
                                                    size="small"
                                                    sx={{
                                                        position: 'absolute',
                                                        top: 8,
                                                        right: 8,
                                                        backgroundColor: 'rgba(0,0,0,.55)',
                                                        color: '#fff',
                                                        '&:hover': { backgroundColor: 'rgba(0,0,0,.75)' },
                                                    }}
                                                >
                                                    <Trash2 size={16} />
                                                </IconButton>
                                            </Box>
                                        ) : (
                                            <>
                                                <Box
                                                    sx={{
                                                        width: 44,
                                                        height: 44,
                                                        borderRadius: '13px',
                                                        margin: '0 auto 10px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        backgroundImage: theme.palette.gradient.brand,
                                                        color: '#fff',
                                                    }}
                                                >
                                                    <ImagePlus size={20} />
                                                </Box>
                                                <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
                                                    Arraste uma imagem aqui ou clique para enviar
                                                </Typography>
                                                <Typography sx={{ fontSize: '11.5px', color: 'text.disabled', mt: 0.5 }}>
                                                    PNG ou JPG · até 5MB
                                                </Typography>
                                            </>
                                        )}
                                        <input
                                            id="image-upload-standalone"
                                            type="file"
                                            hidden
                                            accept="image/*"
                                            onChange={(event) => {
                                                const file = event.target.files[0];
                                                if (file) handleImageUpload(file);
                                            }}
                                        />
                                    </Box>
                                </AccordionSection>
                            </Stack>

                            <Box sx={{ position: { md: 'sticky' }, top: { md: 20 } }}>
                                <Typography
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        fontSize: '10.5px',
                                        fontWeight: 800,
                                        textTransform: 'uppercase',
                                        letterSpacing: '.06em',
                                        color: 'text.disabled',
                                        mb: 1.25,
                                    }}
                                >
                                    <Eye size={13} /> Como aparece na venda
                                </Typography>

                                <Box
                                    sx={{
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        borderRadius: `${theme.customRadius.hero - 2}px`,
                                        backgroundColor: 'background.paper',
                                        p: 2,
                                        mb: 1.75,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            height: 160,
                                            borderRadius: '12px',
                                            mb: 1.5,
                                            overflow: 'hidden',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            backgroundColor: 'info.bg',
                                        }}
                                    >
                                        {previewImage || formik.values.image ? (
                                            <img
                                                src={previewImage || formik.values.image}
                                                alt="Prévia do produto"
                                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                            />
                                        ) : (
                                            <PackageIcon size={26} color={theme.palette.primary.main} />
                                        )}
                                    </Box>
                                    <Typography sx={{ fontSize: 14, fontWeight: 800, mb: 0.75, minHeight: 36 }}>
                                        {formik.values.name || 'Nome do produto'}
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.25 }}>
                                        <Typography sx={{ fontSize: 18, fontWeight: 800, color: 'primary.main' }}>
                                            {salePriceDisplay}
                                        </Typography>
                                        <Box
                                            component="span"
                                            sx={{
                                                fontSize: 11,
                                                fontWeight: 700,
                                                px: '9px',
                                                py: '3px',
                                                borderRadius: '20px',
                                                backgroundColor: isLowStock ? 'warning.bg' : 'info.bg',
                                                color: isLowStock ? 'warning.main' : 'primary.main',
                                            }}
                                        >
                                            {isLowStock ? `${quantityNumberValue} rest.` : `estq ${quantityNumberValue}`}
                                        </Box>
                                    </Box>
                                    <Typography sx={{ fontSize: 11, color: 'text.disabled', mb: 0.5 }}>
                                        Margem estimada: <Box component="b" sx={{ color: 'text.primary' }}>{marginLabel}</Box>
                                    </Typography>
                                    {isBelowExpectedMargin && (
                                        <Typography
                                            sx={{ fontSize: 11, color: 'warning.main', display: 'flex', alignItems: 'center', gap: '4px', mb: 0.5 }}
                                        >
                                            <AlertTriangle size={12} strokeWidth={2} />
                                            {belowExpectedMarginText}
                                        </Typography>
                                    )}
                                    <Box sx={{ height: 8, borderRadius: '6px', backgroundColor: 'background.subtle', overflow: 'hidden' }}>
                                        <Box
                                            sx={{
                                                height: '100%',
                                                width: `${marginPct}%`,
                                                borderRadius: '6px',
                                                background: `linear-gradient(90deg, ${theme.palette.brand2.main}, ${theme.palette.success.main})`,
                                                transition: 'width .3s ease',
                                            }}
                                        />
                                    </Box>

                                    {hasModule('marketplace') && (!isEditing || !hasMercadoLivreListing) && (
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                gap: '14px',
                                                pt: '14px',
                                                mt: 2,
                                                borderTop: '1px solid',
                                                borderColor: 'divider',
                                            }}
                                        >
                                            <Box>
                                                <Stack direction="row" alignItems="center" spacing={0.5}>
                                                    <Typography sx={{ fontSize: 13, fontWeight: 700 }}>Marketplace</Typography>
                                                    <Tooltip title="Ao marcar as opções, o produto será sincronizado automaticamente com o marketplace (Mercado Livre ou outro que estiver marcado). Isso criará um anúncio, editará as informações e atualizará o estoque.">
                                                        <InfoLucideIcon size={14} color={theme.palette.text.disabled} />
                                                    </Tooltip>
                                                </Stack>
                                                <Typography sx={{ fontSize: '11.5px', color: 'text.disabled' }}>
                                                    {mercadoLivreUrl ? (
                                                        <Box
                                                            component="a"
                                                            href={mercadoLivreUrl}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                                                        >
                                                            Ver anúncio no Mercado Livre
                                                        </Box>
                                                    ) : (
                                                        'Sincronizar com o Mercado Livre'
                                                    )}
                                                </Typography>
                                            </Box>
                                            <Tooltip
                                                title={isMarketplaceSwitchDisabled ? 'Preencha "Informações básicas" e "Preço e estoque" primeiro' : ''}
                                                disableHoverListener={!isMarketplaceSwitchDisabled}
                                            >
                                                <span>
                                                    <Switch
                                                        checked={formik.values.mercado_livre_create}
                                                        onChange={handleMarketplaceToggleChange}
                                                        disabled={isMarketplaceSwitchDisabled}
                                                    />
                                                </span>
                                            </Tooltip>
                                        </Box>
                                    )}
                                </Box>

                                <Box
                                    sx={{
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        borderRadius: `${theme.customRadius.hero}px`,
                                        backgroundColor: 'background.paper',
                                        boxShadow: theme.customShadows.md,
                                        p: '18px 20px',
                                        mb: 1.75,
                                    }}
                                >
                                    <PreviewRow label="Código" value={formik.values.code || '—'} />
                                    <PreviewRow label="Categoria" value={selectedCategoryLabel || '—'} />
                                    <PreviewRow label="Fornecedor" value={selectedSupplierLabel || '—'} />
                                    <PreviewRow label="Genérico" value={formik.values.is_generic ? 'Sim' : 'Não'} />
                                    <PreviewRow
                                        label="Mercado Livre"
                                        value={formik.values.mercado_livre_create ? 'Ativo' : 'Não vinculado'}
                                        highlight={formik.values.mercado_livre_create}
                                    />
                                </Box>

                                <Typography sx={{ fontSize: 12, color: 'text.disabled' }}>
                                    Campos com <Box component="span" sx={{ color: 'error.main' }}>*</Box> são obrigatórios.
                                </Typography>
                            </Box>
                        </Box>
                    )}

                    {/* Espaçador para compensar o rodapé fixo abaixo — sem
                        isso o conteúdo final ficaria escondido atrás dele. */}
                    <Box sx={{ height: 'calc(84px + env(safe-area-inset-bottom, 0px))' }} aria-hidden="true" />

                    {/* Ref: prototipo-telas-insercao.html `.action-footer`
                        (linha ~305) — barra fixa na base da viewport, não um
                        card flutuante. `left` acompanha a largura real do
                        menu lateral (`ModernSidebar.js`), inclusive quando
                        recolhido/oculto (mobile). */}
                    <Box
                        sx={{
                            position: 'fixed',
                            left: actionFooterLeft,
                            right: 0,
                            bottom: 0,
                            zIndex: theme.zIndex.appBar,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 2,
                            flexWrap: 'wrap',
                            backgroundColor: 'background.paper',
                            borderTop: '1px solid',
                            borderColor: 'divider',
                            boxShadow: '0 -6px 20px rgba(15,23,42,.06)',
                            px: 3,
                            py: 1.75,
                            pl: 'calc(24px + env(safe-area-inset-left, 0px))',
                            pr: 'calc(24px + env(safe-area-inset-right, 0px))',
                            pb: 'calc(14px + env(safe-area-inset-bottom, 0px))',
                            transition: 'left .2s ease',
                        }}
                    >
                        <Typography sx={{ fontSize: '12.5px', color: 'text.disabled' }}>
                            Revise os dados antes de salvar.
                        </Typography>
                        <Stack direction="row" spacing={1.5}>
                            <Button variant="outlined" onClick={goToList}>
                                Cancelar
                            </Button>
                            <Button
                                variant="contained"
                                onClick={handleStandaloneSaveClick}
                                disabled={loading}
                            >
                                {loading ? 'Salvando...' : (id ? 'Atualizar produto' : 'Salvar produto')}
                            </Button>
                        </Stack>
                    </Box>
                </form>
                )}
            </Box>

            {/* Diálogos */}
            <DialogForm
                iconType="warning"
                open={openDialogRemoveMarketplace}
                title="Atenção"
                confirmButtonText="Sim, remover"
                content="Deseja remover o anúncio deste produto no marketplace?"
                secondaryContent="Essa ação removerá o anúncio no Mercado Livre."
                onSubmit={() => {
                    
                    httpClient.delete(`/stock/${id}/marketplace`)
                        .then(response => {
                            setOpenDialogRemoveMarketplace(false);
                            formik.setFieldValue('mercado_livre_create', false);
                        })
                        .catch(error => {
                            console.error('Error removing marketplace:', error);
                            setMessage('Erro ao remover marketplace');
                            setSeverity('error');
                            setOpenSnackbar(true);
                        });
                }}
                onClose={() => {
                    setOpenDialogRemoveMarketplace(false);
                }}
                onCancel={() => {
                    setOpenDialogRemoveMarketplace(false);
                }}
            />
            <DialogForm
                open={openDialogNewCategory}
                onClose={() => setOpenDialogNewCategory(false)}
                title="Nova Categoria"
                content="Preencha os campos abaixo para criar uma nova categoria."
                onSubmit={(event) => {
                    event.preventDefault();
                    const formData = new FormData(event.currentTarget);
                    const formJson = Object.fromEntries(formData.entries());

                    const name = formJson.name;
                    const description = formJson.description;
                    if (!name) {
                        setErrorCategoryName('Nome é obrigatório');
                        return;
                    }
                    if (!description) {
                        setErrorCategoryDescription('Descrição é obrigatória');
                        return;
                    }

                    const newCategory = {
                        name: name,
                        description: description
                    };

                    httpClient.post('/category', newCategory)
                        .then(response => {
                            const createdCategory = response.data;
                            setCategories([...categories, createdCategory]);
                            formik.setFieldValue('category', createdCategory.id);
                            setCategoryName('');
                            setCategoryDescription('');
                            setErrorCategoryName('');
                            setErrorCategoryDescription('');
                        })
                        .catch(error => {
                            console.error('Error creating category:', error);
                            setMessage('Erro ao criar categoria');
                            setSeverity('error');
                            setOpenSnackbar(true);
                        });
                    setOpenDialogNewCategory(false);
                }}
            >
                <TextField
                    autoFocus
                    id="name"
                    name="name"
                    label="Nome da Categoria"
                    type="text"
                    fullWidth
                    variant="outlined"
                    sx={standaloneFieldSx}
                    value={categoryName}
                    onChange={(e) => setCategoryName(e.target.value)}
                    error={errorCategoryName}
                    helperText={errorCategoryName ? 'Campo obrigatório' : ''}
                />

                <TextField
                    id="description"
                    name="description"
                    label="Descrição da Categoria"
                    type="text"
                    fullWidth
                    variant="outlined"
                    sx={standaloneFieldSx}
                    value={categoryDescription}
                    onChange={(e) => setCategoryDescription(e.target.value)}
                    error={errorCategoryDescription}
                    helperText={errorCategoryDescription ? 'Campo obrigatório' : ''}
                />
            </DialogForm>

            <DialogForm
                open={openDialogNewSubCategory}
                onClose={() => setOpenDialogNewSubCategory(false)}
                title="Nova Subcategoria"
                content="Preencha os campos abaixo para criar uma nova subcategoria."
                onSubmit={(event) => {
                    event.preventDefault();
                    const formData = new FormData(event.currentTarget);
                    const formJson = Object.fromEntries(formData.entries());

                    const name = formJson.name;
                    const description = formJson.description;
                    const profitMarginRaw = formJson.profit_margin;
                    if (!name) {
                        setErrorCategoryName('Nome é obrigatório');
                        return;
                    }
                    if (!description) {
                        setErrorCategoryDescription('Descrição é obrigatória');
                        return;
                    }
                    let profitMarginValue = null;
                    if (profitMarginRaw !== '' && profitMarginRaw != null) {
                        const parsed = parseFloat(profitMarginRaw);
                        if (Number.isNaN(parsed) || parsed < 0 || parsed > 100) {
                            setErrorSubCategoryProfitMargin('Margem deve estar entre 0 e 100');
                            return;
                        }
                        profitMarginValue = parsed;
                    }
                    setErrorSubCategoryProfitMargin('');

                    const newSubCategory = {
                        name: name,
                        description: description,
                        category_id: formik.values.category
                    };
                    if (profitMarginValue !== null) {
                        newSubCategory.profit_margin = profitMarginValue;
                    }

                    httpClient.post('/category/subcategory', newSubCategory)
                        .then(response => {
                            const createdSubCategory = response.data;
                            setSubCategories([...subCategories, createdSubCategory]);
                            formik.setFieldValue('subcategory', createdSubCategory.id);
                            setSubCategoryName('');
                            setSubCategoryDescription('');
                            setSubCategoryProfitMargin('');
                            setErrorSubCategoryName('');
                            setErrorSubCategoryDescription('');
                            setErrorSubCategoryProfitMargin('');
                        })
                        .catch(error => {
                            console.error('Error creating category:', error);
                            setMessage('Erro ao criar categoria');
                            setSeverity('error');
                            setOpenSnackbar(true);
                        }).finally(() => {
                            setOpenDialogNewSubCategory(false);
                        });
                }}
            >
                <TextField
                    autoFocus
                    id="name"
                    name="name"
                    label="Nome da Subcategoria"
                    type="text"
                    fullWidth
                    variant="outlined"
                    sx={standaloneFieldSx}
                    value={subCategoryName}
                    onChange={(e) => setSubCategoryName(e.target.value)}
                    error={errorSubCategoryName}
                    helperText={errorSubCategoryName ? 'Campo obrigatório' : ''}
                />

                <TextField
                    id="description"
                    name="description"
                    label="Descrição da Subcategoria"
                    type="text"
                    fullWidth
                    variant="outlined"
                    sx={standaloneFieldSx}
                    value={subCategoryDescription}
                    onChange={(e) => setSubCategoryDescription(e.target.value)}
                    error={errorSubCategoryDescription}
                    helperText={errorSubCategoryDescription ? 'Campo obrigatório' : ''}
                />

                <TextField
                    id="profit_margin"
                    name="profit_margin"
                    label="Margem de Lucro (%)"
                    type="number"
                    inputProps={{ min: 0, max: 100, step: 0.5 }}
                    fullWidth
                    variant="outlined"
                    sx={standaloneFieldSx}
                    placeholder="Opcional - usa margem da categoria"
                    value={subCategoryProfitMargin}
                    onChange={(e) => {
                        setSubCategoryProfitMargin(e.target.value);
                        if (errorSubCategoryProfitMargin) setErrorSubCategoryProfitMargin('');
                    }}
                    error={Boolean(errorSubCategoryProfitMargin)}
                    helperText={errorSubCategoryProfitMargin || 'Opcional. Deixe em branco para usar a margem da categoria.'}
                />
            </DialogForm>

            <DialogForm
                confirmButtonText="Calcular"
                open={openDialogCalculateSalePrice}
                onClose={() => setOpenDialogCalculateSalePrice(false)}
                title="Calcular Preço de Venda"
                content="Informe o percentual de lucro desejado para calcular o preço de venda."
                onSubmit={(event) => {
                    event.preventDefault();

                    const priceCost = normalizePriceCostForApi(formik.values.cost);
                    const percent = Number.parseInt(String(profitPercentage ?? ''), 10);

                    if (!isValidCostValue(formik.values.cost) || !priceCost) {
                        setMessage('Informe um valor de custo válido antes de calcular.');
                        setSeverity('error');
                        setOpenSnackbar(true);
                        return;
                    }
                    if (Number.isNaN(percent) || percent < 1 || percent > 99) {
                        setMessage('Selecione um percentual de lucro entre 1% e 99%.');
                        setSeverity('error');
                        setOpenSnackbar(true);
                        return;
                    }

                    const calculateSalePrice = {
                        price_cost: priceCost,
                        percent,
                        price_seller: '',
                        subcategory_id: formik.values.subcategory || '',
                        category_id: formik.values.category || '',
                        manual_calc: true
                    };

                    httpClient.post('/stock/calculate-price-seller', calculateSalePrice)
                        .then((response) => {
                            if (response.status !== 200) {
                                const msg = typeof response.data === 'string'
                                    ? response.data
                                    : (response.data?.message || 'Erro ao calcular preço de venda.');
                                setMessage(msg);
                                setSeverity('error');
                                setOpenSnackbar(true);
                                return;
                            }
                            const raw = response.data?.price_seller;
                            if (raw === null || raw === undefined || raw === '') {
                                setMessage('Não foi possível obter o preço calculado. Tente novamente.');
                                setSeverity('error');
                                setOpenSnackbar(true);
                                return;
                            }
                            formik.setFieldValue('sale_price', formatPriceSellerFromApi(raw));
                            setOpenDialogCalculateSalePrice(false);
                        })
                        .catch((error) => {
                            console.error('Error calculating sale price:', error);
                            setMessage('Erro ao calcular preço de venda');
                            setSeverity('error');
                            setOpenSnackbar(true);
                        });
                }}
            >
                <Dropdown
                    label="Percentual de Lucro"
                    data={Array.from({ length: 99 }, (_, i) => ({ id: String(i + 1), name: `${i + 1}%` }))}
                    selectedItem={profitPercentage}
                    handleChange={(value) => setProfitPercentage(value === null || value === undefined ? '' : String(value))}
                    labelVariant="static-micro"
                    variant="outlined"
                    size="medium"
                    sx={standaloneFieldSx}
                />
            </DialogForm>

            <DialogForm
                confirmButtonText="Usar Preço Sugerido"
                open={openDialogSuggestedPrice}
                onClose={() => setOpenDialogSuggestedPrice(false)}
                title="Preço de Venda Sugerido"
                content={`Com base na margem de lucro aplicável (${categoryProfitMargin}%), o preço de venda sugerido é:`}
                onSubmit={(event) => {
                    event.preventDefault();
                    formik.setFieldValue('sale_price', suggestedPrice);
                    setOpenDialogSuggestedPrice(false);
                }}
                onCancel={() => setOpenDialogSuggestedPrice(false)}
            >
                <Box sx={{ 
                    mt: 2, 
                    p: 3, 
                    backgroundColor: theme.palette.mode === 'dark' 
                        ? 'rgba(25, 118, 210, 0.1)' 
                        : 'rgba(25, 118, 210, 0.05)',
                    borderRadius: 2,
                    border: `1px solid ${theme.palette.primary.main}`
                }}>
                    <Typography variant="h4" sx={{ 
                        fontWeight: 700, 
                        color: theme.palette.primary.main,
                        textAlign: 'center'
                    }}>
                        {suggestedPrice || '0,00'}
                    </Typography>
                    <Typography variant="body2" sx={{ 
                        mt: 1, 
                        textAlign: 'center',
                        color: theme.palette.text.secondary
                    }}>
                        Margem de lucro: {categoryProfitMargin}%
                    </Typography>
                </Box>
            </DialogForm>

            <Drawer
                anchor="right"
                open={openDialogMarketplaceAttributes}
                onClose={handleResetMarketplaceDialog}
                PaperProps={{
                    sx: {
                        width: { xs: '100%', sm: 420 },
                        maxWidth: '100vw'
                    }
                }}
            >
                <Box
                    component="form"
                    onSubmit={(event) => {
                        event.preventDefault();
                        handleCloseMarketplaceDialog();
                    }}
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        height: '100%'
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            px: 2,
                            py: 2,
                            borderBottom: 1,
                            borderColor: 'divider'
                        }}
                    >
                        <Typography variant="h6" component="h2">
                            Atributos do Mercado Livre
                        </Typography>
                        <IconButton
                            aria-label="Fechar"
                            onClick={handleResetMarketplaceDialog}
                            size="small"
                        >
                            <CloseIcon />
                        </IconButton>
                    </Box>
                    <Box sx={{ flex: 1, overflow: 'auto', px: 2, py: 2 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Preencha os atributos exigidos pelo Mercado Livre.
                        </Typography>
                        {marketplaceAttributesLoading && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: 2,
                                    py: 4
                                }}
                            >
                                <CircularProgress size={28} />
                                <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                                    Carregando atributos...
                                </Typography>
                            </Box>
                        )}
                        {!marketplaceAttributesLoading && (
                            <Stack spacing={2}>
                                {marketplaceCategoriesLoading ? (
                                    <Skeleton variant="text" width="100%" height={40} />
                                ) : (
                                    <Box>
                                        <Dropdown
                                            label="Categorias"
                                            data={marketplaceCategories}
                                            selectedItem={marketplaceCategoryId}
                                            handleChange={(value) => {
                                                setMarketplaceCategoryId(value);
                                                fetchMarketplaceAttributes(value);
                                            }}
                                            labelVariant="static-micro"
                                            variant="outlined"
                                            size="medium"
                                            sx={standaloneFieldSx}
                                        />
                                        {marketplaceCategories.length === 0 && (
                                            <>
                                                <Alert severity="info" sx={{ mt: 1 }}>
                                                    Nenhuma categoria sugerida para o nome informado.
                                                </Alert>
                                                <NewInlineLink
                                                    label="Não achei a categoria"
                                                    icon={Search}
                                                    onClick={handleOpenCategoryTree}
                                                />
                                            </>
                                        )}
                                    </Box>
                                )}
                                <Dropdown
                                    label="Condição"
                                    data={[
                                        { id: 'new', name: 'Novo' },
                                        { id: 'used', name: 'Usado' }
                                    ]}
                                    selectedItem={marketplaceCondition}
                                    handleChange={(value) => setMarketplaceCondition(value)}
                                    labelVariant="static-micro"
                                    variant="outlined"
                                    size="medium"
                                    sx={standaloneFieldSx}
                                />
                                <Dropdown
                                    label="Tipo do Anúncio"
                                    data={[
                                        { id: 'free', name: 'free' },
                                        { id: 'gold_special', name: 'gold' },
                                        { id: 'gold_pro', name: 'gold pro' }
                                    ]}
                                    selectedItem={marketplaceListingTypeId}
                                    handleChange={(value) => setMarketplaceListingTypeId(value)}
                                    labelVariant="static-micro"
                                    variant="outlined"
                                    size="medium"
                                    sx={standaloneFieldSx}
                                />
                                <TextField
                                    label="Valor de venda no Mercado Livre"
                                    fullWidth
                                    type="text"
                                    value={marketplacePrice}
                                    onChange={(event) => setMarketplacePrice(formatCurrency(event.target.value))}
                                    error={!(parseBRLNumber(marketplacePrice) > 0)}
                                    helperText={
                                        parseBRLNumber(marketplacePrice) > 0
                                            ? 'Não altera o valor de venda cadastrado do produto.'
                                            : 'Informe um valor maior que 0,00.'
                                    }
                                    variant="outlined"
                                    sx={standaloneFieldSx}
                                    InputProps={{
                                        startAdornment: <InputAdornment position="start">R$</InputAdornment>
                                    }}
                                />
                                {marketplaceAttributes.length === 0 && (
                                    <Alert severity="info">Nenhum atributo disponível para este produto.</Alert>
                                )}
                                {marketplaceAttributes.map((attribute) => {
                                    const options = Array.isArray(attribute.values)
                                        ? attribute.values
                                        : (Array.isArray(attribute.value) ? attribute.value : []);
                                    const hasOptions = options.length > 0;
                                    const selectedValue = marketplaceAttributeValues[attribute.id] || '';
                                    const allowedUnits = Array.isArray(attribute.allowed_units) ? attribute.allowed_units : [];
                                    const hasAllowedUnits = allowedUnits.length > 0;
                                    const selectedUnitId = marketplaceAttributeUnits[attribute.id] || '';

                                    if (hasOptions) {
                                        return (
                                            <Dropdown
                                                key={attribute.id}
                                                label={attribute.name}
                                                data={options}
                                                selectedItem={selectedValue}
                                                handleChange={(value) => handleMarketplaceAttributeChange(attribute.id, value)}
                                                mode="autocomplete"
                                                noOptionsText="Nenhuma opção encontrada"
                                                variant="outlined"
                                                size="medium"
                                                sx={standaloneFieldSx}
                                            />
                                        );
                                    }

                                    return (
                                        <Box
                                            key={attribute.id}
                                            sx={{
                                                display: 'flex',
                                                flexDirection: { xs: 'column', sm: 'row' },
                                                gap: 2,
                                                alignItems: { sm: 'flex-end' }
                                            }}
                                        >
                                            <TextField
                                                label={attribute.name}
                                                fullWidth
                                                value={selectedValue}
                                                onChange={(event) =>
                                                    handleMarketplaceAttributeChange(attribute.id, event.target.value)
                                                }
                                                variant="outlined"
                                                sx={standaloneFieldSx}
                                                size="small"
                                            />
                                            {hasAllowedUnits && (
                                                <Dropdown
                                                    label="Unidade"
                                                    data={allowedUnits}
                                                    selectedItem={selectedUnitId}
                                                    handleChange={(value) =>
                                                        handleMarketplaceAttributeUnitChange(attribute.id, value)
                                                    }
                                                    labelVariant="static-micro"
                                                    variant="outlined"
                                                    size="medium"
                                                    sx={{ ...standaloneFieldSx, minWidth: 160 }}
                                                />
                                            )}
                                        </Box>
                                    );
                                })}
                            </Stack>
                        )}
                    </Box>
                    <Box
                        sx={{
                            display: 'flex',
                            gap: 1,
                            justifyContent: 'flex-end',
                            px: 2,
                            py: 2,
                            borderTop: 1,
                            borderColor: 'divider'
                        }}
                    >
                        <Button
                            variant="outlined"
                            onClick={handleResetMarketplaceDialog}
                            startIcon={<CancelIcon />}
                        >
                            Cancelar
                        </Button>
                        <Button
                            type="submit"
                            variant="contained"
                            disabled={marketplaceAttributesLoading || !(parseBRLNumber(marketplacePrice) > 0)}
                            startIcon={<SaveIcon />}
                        >
                            Salvar
                        </Button>
                    </Box>
                </Box>
            </Drawer>

            <DialogForm
                open={categoryTreeOpen}
                onClose={handleCloseCategoryTree}
                onCancel={handleCloseCategoryTree}
                onSubmit={(event) => event.preventDefault()}
                title="Selecionar categoria"
                subtitle="Navegue pelas categorias do Mercado Livre até encontrar a categoria correta."
                hideConfirmButton
                cancelButtonText="Fechar"
            >
                <Breadcrumbs separator={<ChevronRight size={12} />} sx={{ fontSize: '12.5px', mb: 1 }}>
                    <Link
                        component="button"
                        type="button"
                        underline={categoryTreePath.length ? 'hover' : 'none'}
                        color={categoryTreePath.length ? 'primary' : 'text.secondary'}
                        disabled={categoryTreePath.length === 0}
                        onClick={() => handleCategoryTreeBreadcrumbClick(-1)}
                        sx={{ fontSize: '12.5px' }}
                    >
                        Início
                    </Link>
                    {categoryTreePath.map((node, index) => {
                        const isLast = index === categoryTreePath.length - 1;
                        return (
                            <Link
                                key={node.id}
                                component="button"
                                type="button"
                                underline={isLast ? 'none' : 'hover'}
                                color={isLast ? 'text.secondary' : 'primary'}
                                disabled={isLast}
                                onClick={() => handleCategoryTreeBreadcrumbClick(index)}
                                sx={{ fontSize: '12.5px' }}
                            >
                                {node.name}
                            </Link>
                        );
                    })}
                </Breadcrumbs>

                {categoryTreeLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                        <CircularProgress size={24} />
                    </Box>
                ) : (
                    <List dense sx={{ maxHeight: 320, overflowY: 'auto' }}>
                        {categoryTreeNodes.length === 0 && (
                            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                                Nenhuma subcategoria encontrada.
                            </Typography>
                        )}
                        {categoryTreeNodes.map((node) => (
                            <ListItemButton
                                key={node.id}
                                onClick={() => handleSelectCategoryTreeNode(node)}
                                sx={{ borderRadius: 1 }}
                            >
                                <ListItemText primary={node.name} />
                                <ChevronRight size={16} />
                            </ListItemButton>
                        ))}
                    </List>
                )}
            </DialogForm>
        </>
    );

    if (embedded) {
        return (
            <>
                <Box
                    sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                        backgroundColor: theme.palette.background.default
                    }}
                >
                    <Box sx={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                        {formContent}
                    </Box>
                </Box>
                <BarcodeScannerDialog
                    open={scannerOpen}
                    onClose={() => setScannerOpen(false)}
                    onDetected={handleBarcodeDetected}
                />
                <Snackbar
                    open={openSnackbar}
                    autoHideDuration={6000}
                    onClose={() => setOpenSnackbar(false)}
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                    sx={{ mt: { xs: 7, sm: 8 } }}
                >
                    <Alert
                        onClose={() => setOpenSnackbar(false)}
                        severity={severity}
                        variant="filled"
                        sx={{ width: '100%' }}
                    >
                        {message}
                    </Alert>
                </Snackbar>
                <Backdrop
                    open={loading}
                    sx={{
                        zIndex: (theme) => theme.zIndex.modal + 1,
                        color: '#fff',
                        backgroundColor: theme.palette.mode === 'dark'
                            ? 'rgba(0, 0, 0, 0.65)'
                            : 'rgba(17, 24, 39, 0.45)',
                        backdropFilter: 'blur(2px)'
                    }}
                >
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
                        <CircularProgress color="inherit" />
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            Salvando produto...
                        </Typography>
                    </Box>
                </Backdrop>
            </>
        );
    }

    return (
        <PageScaffold
            loading={loading}
            title={id ? 'Editar Produto' : 'Novo Produto'}
            titlePageTarget={id ? 'Editar' : 'Novo'}
            links={breadcrumbs}
            openSnackbar={openSnackbar}
            setOpenSnackbar={setOpenSnackbar}
            message={message}
            severity={severity}
        >
            {formContent}
            <BarcodeScannerDialog
                open={scannerOpen}
                onClose={() => setScannerOpen(false)}
                onDetected={handleBarcodeDetected}
            />
        </PageScaffold>
    );
}